/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Plus,
  Search,
  Tag as TagIcon,
  Heart,
  Clock,
  Trash2,
  Undo2,
  Redo2,
  Pin,
  Maximize2,
  LayoutGrid,
  Grid,
  Lock,
  ChevronDown,
  Sparkles,
  Eye,
  Sliders,
  Maximize,
  Compass,
  Edit2,
  User,
  Check,
  MessageSquare
} from 'lucide-react';
import { NoteCard, Notebook } from '../../types';
import { Point, ToolType, CanvasSheetSize, WhiteboardElement } from '../canvas/WhiteboardTypes';
import { WhiteboardToolbar } from '../canvas/tools/WhiteboardToolbar';
import { BoardGroup, BoardConnector, BoardDecoration, BackgroundStyle } from './WhiteboardTypes';
import { Minimap } from './Minimap';
import { BrushSelector } from '../canvas/tools/BrushSelector';
import { EraserControl } from '../canvas/tools/EraserControl';

// 10 Beautiful Modern Pastel Sticky Note Colors
const PASTEL_COLORS = [
  { name: 'Yellow', fill: '#FFF4B8', text: '#5F521E', border: '#D9C873' },
  { name: 'Pink', fill: '#FFD6E8', text: '#662B44', border: '#D998B5' },
  { name: 'Blue', fill: '#D6F4FF', text: '#1F4759', border: '#9BCDE0' },
  { name: 'Green', fill: '#DDF7D5', text: '#2A4D22', border: '#A4D998' },
  { name: 'Purple', fill: '#E8DEFF', text: '#3E2F66', border: '#B5A0E6' },
  { name: 'Orange', fill: '#FFE3D1', text: '#663B1F', border: '#E6A780' },
  { name: 'Peach', fill: '#FFEAD2', text: '#66451F', border: '#E6B380' },
  { name: 'Mint', fill: '#E2FCEF', text: '#1E593D', border: '#A2E6C3' },
  { name: 'Lavender', fill: '#F0E5FF', text: '#4D2F66', border: '#C9A0E6' },
  { name: 'Sky Blue', fill: '#E6F4FE', text: '#214966', border: '#A0CAE6' },
];

interface NoteboardWorkspaceProps {
  cards: NoteCard[];
  setCards: React.Dispatch<React.SetStateAction<NoteCard[]>>;
  syncCards?: (newCards: NoteCard[]) => void;
  notebooks: Notebook[];
  selectedNotebookId?: string | null;
  setEditingCard: (card: NoteCard | null) => void;
  editingCard?: NoteCard | Partial<NoteCard> | null;
  isDarkMode: boolean;
  onClose: () => void;
}

export const NoteboardWorkspace: React.FC<NoteboardWorkspaceProps> = ({
  cards: parentCards,
  setCards: parentSetCards,
  syncCards,
  notebooks,
  selectedNotebookId = null,
  setEditingCard,
  editingCard = null,
  isDarkMode,
  onClose,
}) => {
  const activeNotebookId = selectedNotebookId || notebooks[0]?.id || 'global';

  const [cards, setCardsState] = useState<NoteCard[]>(parentCards);

  // Sync with parent cards updates
  useEffect(() => {
    setCardsState(parentCards);
  }, [parentCards]);

  const setCards = (update: NoteCard[] | ((prev: NoteCard[]) => NoteCard[])) => {
    setCardsState(update);
  };

  // Whiteboard Canvas Configuration States
  const [zoom, setZoom] = useState<number>(0.85);
  const [pan, setPan] = useState<Point>({ x: 150, y: 80 });
  const [isPanning, setIsPanning] = useState(false);
  const [spacePressed, setSpacePressed] = useState(false);
  const [backgroundStyle, setBackgroundStyle] = useState<BackgroundStyle>('dots');
  const [gridVisible, setGridVisible] = useState(true);

  // Whiteboard Elements states (stored in localStorage per notebook)
  const [decorations, setDecorations] = useState<BoardDecoration[]>([]);
  const [connectors, setConnectors] = useState<BoardConnector[]>([]);
  const [groups, setGroups] = useState<BoardGroup[]>([]);

  // Selection & Interactions states
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeTool, setActiveTool] = useState<ToolType>('select');
  const [activeColor, setActiveColor] = useState<string>('#3b82f6');
  
  // Drag selection marquee box state
  const [marqueeStart, setMarqueeStart] = useState<Point | null>(null);
  const [marqueeEnd, setMarqueeEnd] = useState<Point | null>(null);

  // Active interaction states
  const [dragStart, setDragStart] = useState<Point | null>(null);
  const [dragOffsets, setDragOffsets] = useState<Record<string, Point>>({});
  const [isResizingId, setIsResizingId] = useState<string | null>(null);
  const [resizingHandle, setResizingHandle] = useState<'tl' | 'tr' | 'bl' | 'br' | null>(null);
  const [draggedGroupId, setDraggedGroupId] = useState<string | null>(null);

  // Animation states
  const [draggingIds, setDraggingIds] = useState<string[]>([]);
  const [justDroppedId, setJustDroppedId] = useState<string | null>(null);

  // Connector drawing state
  const [activeConnectorSource, setActiveConnectorSource] = useState<{
    id: string;
    edge: 'top' | 'right' | 'bottom' | 'left';
  } | null>(null);
  const [connectorMousePos, setConnectorMousePos] = useState<Point | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);

  // Inline editing states
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [textEditValue, setTextEditValue] = useState('');
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [titleEditValue, setTitleEditValue] = useState('');

  // Drawing states
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPathPoints, setCurrentPathPoints] = useState<Point[]>([]);

  // Comment thread states
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [newCommentAuthor, setNewCommentAuthor] = useState('');
  const [newCommentText, setNewCommentText] = useState('');

  // Brush & Eraser properties state
  const [penWidth, setPenWidth] = useState<number>(4);
  const [highlighterWidth, setHighlighterWidth] = useState<number>(12);
  const brushWidth = activeTool === 'highlighter' ? highlighterWidth : penWidth;
  const setBrushWidth = activeTool === 'highlighter' ? setHighlighterWidth : setPenWidth;
  const [eraserWidth, setEraserWidth] = useState<number>(12);
  const [mouseScreenPos, setMouseScreenPos] = useState<Point | null>(null);
  const [contextMenuPos, setContextMenuPos] = useState<Point | null>(null);
  const [contextMenuTarget, setContextMenuTarget] = useState<{
    type: 'canvas' | 'card' | 'decoration';
    id?: string;
  } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  const BRUSH_COLORS = [
    '#000000', // Black
    '#ef4444', // Red
    '#3b82f6', // Ocean Blue
    '#10b981', // Forest Green
    '#f59e0b', // Amber/Yellow
    '#8b5cf6', // Violet
    '#ec4899', // Hot Pink
    '#64748b'  // Slate Gray
  ];

  // Undo/Redo stack history tracking
  interface BoardHistoryState {
    decorations: BoardDecoration[];
    connectors: BoardConnector[];
    groups: BoardGroup[];
    cards: NoteCard[];
  }
  const [undoStack, setUndoStack] = useState<BoardHistoryState[]>([]);
  const [redoStack, setRedoStack] = useState<BoardHistoryState[]>([]);

  // Filtering & Search
  const [searchQuery, setSearchQuery] = useState('');

  // References
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const panStart = useRef<Point>({ x: 0, y: 0 });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const lastClickRef = useRef<{ id: string; time: number } | null>(null);
  const initialResizingGroupRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
  const initialResizingChildrenRef = useRef<Record<string, { x: number; y: number; width: number; height: number; fontSize?: number }>>({});

  // Load canvas state on mount or notebook change
  useEffect(() => {
    const suffix = activeNotebookId;
    const cachedDec = localStorage.getItem(`notebook_clone_board_decorations_${suffix}`);
    const cachedConn = localStorage.getItem(`notebook_clone_board_connectors_${suffix}`);
    const cachedGroups = localStorage.getItem(`notebook_clone_board_groups_${suffix}`);
    const cachedSettings = localStorage.getItem(`notebook_clone_board_settings_${suffix}`);

    if (cachedDec) setDecorations(JSON.parse(cachedDec));
    else setDecorations([]);

    if (cachedConn) setConnectors(JSON.parse(cachedConn));
    else setConnectors([]);

    if (cachedGroups) setGroups(JSON.parse(cachedGroups));
    else setGroups([]);

    if (cachedSettings) {
      const settings = JSON.parse(cachedSettings);
      if (settings.zoom) setZoom(settings.zoom);
      if (settings.pan) setPan(settings.pan);
      if (settings.backgroundStyle) setBackgroundStyle(settings.backgroundStyle);
      if (settings.gridVisible !== undefined) setGridVisible(settings.gridVisible);
    } else {
      setZoom(0.85);
      setPan({ x: 150, y: 80 });
      setBackgroundStyle('dots');
      setGridVisible(true);
    }

    setSelectedIds([]);
  }, [activeNotebookId]);

  // Auto layout active cards that don't have board positions yet
  useEffect(() => {
    const activeCards = cards.filter(c => !c.isDeleted && (selectedNotebookId ? c.notebookId === selectedNotebookId : true));
    let hasUpdated = false;
    const updated = cards.map((c, index) => {
      if (!c.isDeleted && (selectedNotebookId ? c.notebookId === selectedNotebookId : true)) {
        if (c.boardX === undefined || c.boardY === undefined) {
          hasUpdated = true;
          // Arrange in a grid starting at x: 200, y: 200
          const cols = 4;
          const gridX = (index % cols) * 260 + 200;
          const gridY = Math.floor(index / cols) * 240 + 200;
          return {
            ...c,
            boardX: gridX,
            boardY: gridY,
            boardWidth: c.boardWidth || 200,
            boardHeight: c.boardHeight || 180,
          };
        }
      }
      return c;
    });

    if (hasUpdated) {
      setCards(updated);
    }
  }, [cards, selectedNotebookId]);

  // Persist modifications
  const saveCanvasState = (
    newDec: BoardDecoration[],
    newConn: BoardConnector[],
    newGroups: BoardGroup[],
    newCards?: NoteCard[]
  ) => {
    const suffix = activeNotebookId;
    localStorage.setItem(`notebook_clone_board_decorations_${suffix}`, JSON.stringify(newDec));
    localStorage.setItem(`notebook_clone_board_connectors_${suffix}`, JSON.stringify(newConn));
    localStorage.setItem(`notebook_clone_board_groups_${suffix}`, JSON.stringify(newGroups));
    
    const settings = { zoom, pan, backgroundStyle, gridVisible };
    localStorage.setItem(`notebook_clone_board_settings_${suffix}`, JSON.stringify(settings));

    const targetCards = newCards || cards;
    parentSetCards(targetCards);
    if (syncCards) {
      syncCards(targetCards);
    }
  };

  const pushToHistory = (overrideState?: BoardHistoryState) => {
    const currentState: BoardHistoryState = {
      decorations: [...decorations],
      connectors: [...connectors],
      groups: [...groups],
      cards: [...cards]
    };
    setUndoStack(prev => [...prev, overrideState || currentState]);
    setRedoStack([]);
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const previous = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    
    // Save current to redo stack
    setRedoStack(prev => [...prev, {
      decorations: [...decorations],
      connectors: [...connectors],
      groups: [...groups],
      cards: [...cards]
    }]);

    setDecorations(previous.decorations);
    setConnectors(previous.connectors);
    setGroups(previous.groups);
    setCards(previous.cards);
    saveCanvasState(previous.decorations, previous.connectors, previous.groups, previous.cards);
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));

    // Save current to undo stack
    setUndoStack(prev => [...prev, {
      decorations: [...decorations],
      connectors: [...connectors],
      groups: [...groups],
      cards: [...cards]
    }]);

    setDecorations(next.decorations);
    setConnectors(next.connectors);
    setGroups(next.groups);
    setCards(next.cards);
    saveCanvasState(next.decorations, next.connectors, next.groups, next.cards);
  };

  // Keyboard Spacebar drag pan listeners
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeEl = document.activeElement;
      const isTyping = activeEl && (
        activeEl.tagName === 'INPUT' ||
        activeEl.tagName === 'TEXTAREA' ||
        (activeEl as HTMLElement).isContentEditable ||
        activeEl.closest('[contenteditable="true"]') !== null
      );
      if (isTyping) return;

      if (e.key.toLowerCase() === 'z' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
        return;
      }
      if (e.key.toLowerCase() === 'y' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleRedo();
        return;
      }

      if (e.code === 'Space') {
        setSpacePressed(true);
        e.preventDefault();
      }
      if (e.key.toLowerCase() === 'n') {
        setActiveTool('sticky');
      }
      if (e.key.toLowerCase() === 't') {
        setActiveTool('text');
      }
      if (e.key.toLowerCase() === 'p') {
        setActiveTool('pen');
      }
      if (e.key.toLowerCase() === 'h') {
        setActiveTool('highlighter');
      }
      if (e.key.toLowerCase() === 'e') {
        setActiveTool('eraser');
      }
      if (e.key.toLowerCase() === 'c') {
        setActiveTool('comment');
      }
      if (e.key.toLowerCase() === 'i') {
        setActiveTool('image');
      }
      if (e.key.toLowerCase() === 'v') {
        setActiveTool('select');
      }
      if (e.key === 'Escape') {
        setSelectedIds([]);
        setActiveConnectorSource(null);
        setEditingTextId(null);
        setEditingGroupId(null);
        setContextMenuPos(null);
        setContextMenuTarget(null);
      }
      if ((e.key === 'Backspace' || e.key === 'Delete') && selectedIds.length > 0) {
        pushToHistory();
        const idsToDelete = new Set(selectedIds);
        
        // Delete decorators
        const nextDec = decorations.filter(d => !idsToDelete.has(d.id));
        
        // Delete connectors
        const nextConn = connectors.filter(c => !idsToDelete.has(c.id) && !idsToDelete.has(c.fromId) && !idsToDelete.has(c.toId));
        
        // Delete groups
        const nextGroups = groups.filter(g => !idsToDelete.has(g.id));
        
        // Mark cards as deleted
        const nextCards = cards.map(c => idsToDelete.has(c.id) ? { ...c, isDeleted: true, deletedAt: Date.now() } : c);

        setDecorations(nextDec);
        setConnectors(nextConn);
        setGroups(nextGroups);
        saveCanvasState(nextDec, nextConn, nextGroups, nextCards);
        setSelectedIds([]);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setSpacePressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [selectedIds, decorations, connectors, groups, cards, undoStack, redoStack]);

  // Click-away listener to dismiss right-click properties context menu
  useEffect(() => {
    if (!contextMenuPos) return;

    const handleGlobalClick = (e: PointerEvent | MouseEvent) => {
      const menuEl = document.getElementById('whiteboard-context-menu-wrapper');
      if (menuEl && !menuEl.contains(e.target as Node)) {
        setContextMenuPos(null);
        setContextMenuTarget(null);
      }
    };

    window.addEventListener('pointerdown', handleGlobalClick);
    return () => {
      window.removeEventListener('pointerdown', handleGlobalClick);
    };
  }, [contextMenuPos]);

  // Trigger file upload picker immediately when image tool is selected
  useEffect(() => {
    if (activeTool === 'image') {
      fileInputRef.current?.click();
      setActiveTool('select');
    }
  }, [activeTool]);

  // Isolate mouse-wheel zoom
  useEffect(() => {
    const workspace = workspaceRef.current;
    if (!workspace) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const zoomIntensity = 0.06;
      const isZoomIn = e.deltaY < 0;
      const clientX = e.clientX;
      const clientY = e.clientY;

      setZoom((prevZoom) => {
        const nextZoom = isZoomIn
          ? Math.min(4.0, prevZoom + zoomIntensity)
          : Math.max(0.25, prevZoom - zoomIntensity);

        setPan((prevPan) => {
          const rect = workspace.getBoundingClientRect();
          const canvasPtBeforeX = (clientX - rect.left - prevPan.x) / prevZoom;
          const canvasPtBeforeY = (clientY - rect.top - prevPan.y) / prevZoom;

          const newPanX = clientX - rect.left - canvasPtBeforeX * nextZoom;
          const newPanY = clientY - rect.top - canvasPtBeforeY * nextZoom;

          return { x: newPanX, y: newPanY };
        });

        return nextZoom;
      });
    };

    workspace.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      workspace.removeEventListener('wheel', onWheel);
    };
  }, []);

  // Screen Coordinates to Canvas coordinates mapping
  const screenToCanvas = (screenX: number, screenY: number): Point => {
    if (!workspaceRef.current) return { x: 0, y: 0 };
    const rect = workspaceRef.current.getBoundingClientRect();
    return {
      x: (screenX - rect.left - pan.x) / zoom,
      y: (screenY - rect.top - pan.y) / zoom,
    };
  };

  const erasePointsFromPath = (
    d: BoardDecoration,
    eraserCanvasPt: Point,
    canvasRadius: number
  ): BoardDecoration[] => {
    if (!d.points || d.points.length === 0) return [];
    
    const segments: Point[][] = [];
    let currentSegment: Point[] = [];
    
    for (const pt of d.points) {
      const dist = Math.hypot(pt.x - eraserCanvasPt.x, pt.y - eraserCanvasPt.y);
      const inside = dist <= canvasRadius;
      
      if (inside) {
        if (currentSegment.length > 0) {
          segments.push(currentSegment);
          currentSegment = [];
        }
      } else {
        currentSegment.push(pt);
      }
    }
    if (currentSegment.length > 0) {
      segments.push(currentSegment);
    }
    
    return segments
      .filter(seg => seg.length > 0)
      .map((seg, index) => {
        const xs = seg.map(p => p.x);
        const ys = seg.map(p => p.y);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...xs);
        const maxY = Math.max(...ys);
        
        return {
          ...d,
          id: index === 0 ? d.id : `draw-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          x: minX,
          y: minY,
          width: maxX - minX,
          height: maxY - minY,
          points: seg,
        };
      });
  };

  const startResizingGroup = (groupId: string) => {
    const group = groups.find(x => x.id === groupId);
    if (!group) return;

    initialResizingGroupRef.current = {
      x: group.x,
      y: group.y,
      width: group.width,
      height: group.height,
    };

    const children: Record<string, { x: number; y: number; width: number; height: number; fontSize?: number }> = {};

    // Find all cards inside this group container boundary
    cards.forEach(c => {
      if (!c.isDeleted && c.boardX !== undefined && c.boardY !== undefined) {
        if (
          c.boardX >= group.x &&
          c.boardX <= group.x + group.width &&
          c.boardY >= group.y &&
          c.boardY <= group.y + group.height
        ) {
          children[c.id] = {
            x: c.boardX,
            y: c.boardY,
            width: c.boardWidth || 200,
            height: c.boardHeight || 180,
            fontSize: c.fontSize,
          };
        }
      }
    });

    // Find all decorators inside this group boundary
    decorations.forEach(d => {
      if (
        d.x >= group.x &&
        d.x <= group.x + group.width &&
        d.y >= group.y &&
        d.y <= group.y + group.height
      ) {
        children[d.id] = {
          x: d.x,
          y: d.y,
          width: d.width,
          height: d.height,
          fontSize: d.fontSize,
        };
      }
    });

    initialResizingChildrenRef.current = children;
  };

  // Find element by ID (either a NoteCard, BoardDecoration, or BoardGroup)
  const findElementById = (id: string) => {
    const card = cards.find(c => c.id === id && !c.isDeleted);
    if (card) return { ...card, type: 'sticky' } as any;
    
    const dec = decorations.find(d => d.id === id);
    if (dec) return dec;

    const grp = groups.find(g => g.id === id);
    if (grp) return { ...grp, type: 'group' } as any;

    return null;
  };

  // Helper to retrieve nearest connector node edges coordinates
  const getNodeEdgeCoords = (elId: string, edge: 'top' | 'right' | 'bottom' | 'left'): Point => {
    const el = findElementById(elId);
    if (!el) return { x: 0, y: 0 };

    const x = el.boardX !== undefined ? el.boardX : el.x;
    const y = el.boardY !== undefined ? el.boardY : el.y;
    const width = el.boardWidth !== undefined ? el.boardWidth : el.width;
    const height = el.boardHeight !== undefined ? el.boardHeight : el.height;

    switch (edge) {
      case 'top': return { x: x + width / 2, y };
      case 'right': return { x: x + width, y: y + height / 2 };
      case 'bottom': return { x: x + width / 2, y: y + height };
      case 'left': return { x, y: y + height / 2 };
    }
  };

  // Drag selection checking
  const isElementInMarquee = (
    elX: number,
    elY: number,
    elW: number,
    elH: number,
    mStart: Point,
    mEnd: Point
  ): boolean => {
    const minX = Math.min(mStart.x, mEnd.x);
    const maxX = Math.max(mStart.x, mEnd.x);
    const minY = Math.min(mStart.y, mEnd.y);
    const maxY = Math.max(mStart.y, mEnd.y);

    return (
      elX + elW >= minX &&
      elX <= maxX &&
      elY + elH >= minY &&
      elY <= maxY
    );
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!['pen', 'highlighter', 'eraser'].includes(activeTool)) {
      // Right click properties should only work for Pen, Highlighter, and Eraser drawing tools
      return;
    }
    if (!workspaceRef.current) return;
    const rect = workspaceRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setContextMenuTarget({ type: 'canvas' });
    setContextMenuPos({ x, y });
  };

  const getContextMenuStyles = () => {
    if (!contextMenuPos || !workspaceRef.current) return {};
    const menuWidth = 220; // estimated width
    const menuHeight = 250; // estimated height
    const wWidth = workspaceRef.current.clientWidth;
    const wHeight = workspaceRef.current.clientHeight;

    let left = contextMenuPos.x;
    let top = contextMenuPos.y;

    if (left + menuWidth > wWidth) {
      left = wWidth - menuWidth - 16;
    }
    if (top + menuHeight > wHeight) {
      top = wHeight - menuHeight - 16;
    }

    return {
      left: Math.max(16, left),
      top: Math.max(16, top),
    };
  };

  // Workspace pointer triggers
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const isBgClick = 
      target.id === 'sticky-board-whiteboard-viewport' || 
      target.classList.contains('canvas-container') ||
      target.id === 'whiteboard-canvas-background' ||
      target.id === 'whiteboard-grid-pattern' ||
      (target.closest('#sticky-board-whiteboard-viewport') !== null && 
       (target.tagName === 'svg' || target.tagName === 'DIV' || target.tagName === 'rect' || target.tagName === 'circle') &&
       !target.closest('[data-element-id]') && 
       !target.closest('button') && 
       !target.closest('input') && 
       !target.closest('select') && 
       !target.closest('textarea'));

    const isDrawingIgnition = 
      isBgClick || 
      (target.closest('[data-element-id]') !== null && 
       !target.closest('button') && 
       !target.closest('input') && 
       !target.closest('select') && 
       !target.closest('textarea'));

    const canvasPt = screenToCanvas(e.clientX, e.clientY);

    // Close context menu on left click
    if (e.button === 0) {
      setContextMenuPos(null);
      setContextMenuTarget(null);
    }

    // 1. Hand tool panning or Spacebar holds
    if (activeTool === 'hand' || spacePressed || e.button === 1) {
      setIsPanning(true);
      panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
      workspaceRef.current?.setPointerCapture(e.pointerId);
      return;
    }

    // 2. Connector Arrow tool drag ignition from any element
    if (activeTool === 'arrow' && e.button === 0) {
      const elementWrapper = target.closest('[data-element-id]');
      if (elementWrapper) {
        const id = elementWrapper.getAttribute('data-element-id');
        if (id) {
          const clickedEl = findElementById(id);
          if (clickedEl) {
            const tx = clickedEl.boardX !== undefined ? clickedEl.boardX : clickedEl.x;
            const ty = clickedEl.boardY !== undefined ? clickedEl.boardY : clickedEl.y;
            const tw = clickedEl.boardWidth !== undefined ? clickedEl.boardWidth : clickedEl.width;
            const th = clickedEl.boardHeight !== undefined ? clickedEl.boardHeight : clickedEl.height;

            const distances = {
              top: Math.abs(canvasPt.y - ty),
              right: Math.abs(canvasPt.x - (tx + tw)),
              bottom: Math.abs(canvasPt.y - (ty + th)),
              left: Math.abs(canvasPt.x - tx),
            };

            const sorted = Object.entries(distances).sort((a, b) => a[1] - b[1]);
            const startEdge = sorted[0][0] as 'top' | 'right' | 'bottom' | 'left';

            setActiveConnectorSource({ id, edge: startEdge });
            setConnectorMousePos(canvasPt);
            workspaceRef.current?.setPointerCapture(e.pointerId);
            return;
          }
        }
      }
    }

    // Eraser Click Ignition
    if (activeTool === 'eraser' && e.button === 0) {
      const canvasRadius = (eraserWidth / 2) / zoom;
      
      let nextDec: BoardDecoration[] = [];
      let changed = false;
      
      for (const dec of decorations) {
        if (dec.type === 'path') {
          // Bounding box overlap check
          const overlap = 
            dec.x - canvasRadius <= canvasPt.x &&
            canvasPt.x <= dec.x + dec.width + canvasRadius &&
            dec.y - canvasRadius <= canvasPt.y &&
            canvasPt.y <= dec.y + dec.height + canvasRadius;
            
          if (overlap) {
            const splitDecs = erasePointsFromPath(dec, canvasPt, canvasRadius);
            const isModified = splitDecs.length !== 1 || 
              (splitDecs.length === 1 && splitDecs[0].points?.length !== dec.points?.length);
              
            if (isModified) {
              changed = true;
            }
            nextDec.push(...splitDecs);
          } else {
            nextDec.push(dec);
          }
        } else {
          nextDec.push(dec);
        }
      }
      
      if (changed) {
        pushToHistory();
        setDecorations(nextDec);
        saveCanvasState(nextDec, connectors, groups, cards);
      }
      
      workspaceRef.current?.setPointerCapture(e.pointerId);
      return;
    }

    // Pen & Highlighter Drawing Ignition
    if ((activeTool === 'pen' || activeTool === 'highlighter') && isDrawingIgnition) {
      setIsDrawing(true);
      setCurrentPathPoints([canvasPt]);
      workspaceRef.current?.setPointerCapture(e.pointerId);
      return;
    }

    // Dismiss active text input focus
    if (isBgClick) {
      setEditingTextId(null);
      setEditingGroupId(null);
    }

    // 2. Connector line drawing trigger from edge handle
    if (activeConnectorSource) {
      return;
    }

    // 3. Selection Box Marquee creation
    if (activeTool === 'select' && isBgClick) {
      setSelectedIds([]);
      setMarqueeStart(canvasPt);
      setMarqueeEnd(canvasPt);
      workspaceRef.current?.setPointerCapture(e.pointerId);
      return;
    }

    // 4. One-click deposit triggers
    if (isBgClick && ['text', 'sticky', 'rect', 'circle', 'triangle', 'diamond', 'comment'].includes(activeTool)) {
      pushToHistory();
      const elId = `el-${Date.now()}`;

      if (activeTool === 'comment') {
        const newComment: BoardDecoration = {
          id: elId,
          type: 'comment',
          x: canvasPt.x - 18,
          y: canvasPt.y - 18,
          width: 36,
          height: 36,
          color: '#3b82f6',
          comments: [
            {
              id: `cSub-${Date.now()}`,
              author: 'Guest',
              text: 'Write feedback remarks...',
              timestamp: Date.now()
            }
          ]
        };
        const nextDec = [...decorations, newComment];
        setDecorations(nextDec);
        saveCanvasState(nextDec, connectors, groups);
        setSelectedIds([newComment.id]);
        setActiveCommentId(newComment.id);
        setActiveTool('select');
        return;
      }
      
      if (activeTool === 'sticky') {
        const pastel = PASTEL_COLORS[Math.floor(Math.random() * PASTEL_COLORS.length)];
        const newSticky: NoteCard = {
          id: elId,
          notebookId: activeNotebookId,
          type: 'text',
          title: '',
          colorClass: 'bg-amber-50 dark:bg-amber-955/20 text-slate-900 border-amber-200' as any,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          isFavorite: false,
          isLocked: false,
          tags: [],
          textContent: '',
          boardX: canvasPt.x - 100,
          boardY: canvasPt.y - 90,
          boardWidth: 200,
          boardHeight: 180,
          boardColor: pastel.fill,
        };
        const nextCards = [newSticky, ...cards];
        setCards(nextCards);
        setSelectedIds([newSticky.id]);
        saveCanvasState(decorations, connectors, groups, nextCards);
        setEditingCard(newSticky);
      } else if (activeTool === 'text') {
        const newText: BoardDecoration = {
          id: elId,
          type: 'text',
          x: canvasPt.x - 90,
          y: canvasPt.y - 20,
          width: 180,
          height: 40,
          color: activeColor,
          text: 'Text Label',
        };
        const nextDec = [...decorations, newText];
        setDecorations(nextDec);
        setSelectedIds([newText.id]);
        saveCanvasState(nextDec, connectors, groups);
        setEditingTextId(newText.id);
        setTextEditValue('Text Label');
      } else {
        // Shapes deposit
        const newShape: BoardDecoration = {
          id: elId,
          type: 'shape',
          x: canvasPt.x - 75,
          y: canvasPt.y - 50,
          width: 150,
          height: 100,
          color: activeColor,
          fillColor: activeColor + '15', // Translucent tint
          shapeType: activeTool as any,
          text: '',
        };
        const nextDec = [...decorations, newShape];
        setDecorations(nextDec);
        setSelectedIds([newShape.id]);
        saveCanvasState(nextDec, connectors, groups);
      }

      setActiveTool('select'); // revert tool selection
      return;
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    setMouseScreenPos({ x: e.clientX, y: e.clientY });
    const canvasPt = screenToCanvas(e.clientX, e.clientY);

    // 1. Pan Workspace offset
    if (isPanning) {
      setPan({
        x: e.clientX - panStart.current.x,
        y: e.clientY - panStart.current.y,
      });
      return;
    }

    // Pen & Highlighter Drawing Path update
    if (isDrawing && (activeTool === 'pen' || activeTool === 'highlighter')) {
      setCurrentPathPoints(prev => [...prev, canvasPt]);
      return;
    }

    // Drag Eraser Hover point-by-point math erasing logic
    if (activeTool === 'eraser' && e.buttons === 1) {
      const canvasRadius = (eraserWidth / 2) / zoom;
      
      let nextDec: BoardDecoration[] = [];
      let changed = false;
      
      for (const dec of decorations) {
        if (dec.type === 'path') {
          // Bounding box overlap check
          const overlap = 
            dec.x - canvasRadius <= canvasPt.x &&
            canvasPt.x <= dec.x + dec.width + canvasRadius &&
            dec.y - canvasRadius <= canvasPt.y &&
            canvasPt.y <= dec.y + dec.height + canvasRadius;
            
          if (overlap) {
            const splitDecs = erasePointsFromPath(dec, canvasPt, canvasRadius);
            const isModified = splitDecs.length !== 1 || 
              (splitDecs.length === 1 && splitDecs[0].points?.length !== dec.points?.length);
              
            if (isModified) {
              changed = true;
            }
            nextDec.push(...splitDecs);
          } else {
            nextDec.push(dec);
          }
        } else {
          nextDec.push(dec);
        }
      }
      
      if (changed) {
        pushToHistory();
        setDecorations(nextDec);
        saveCanvasState(nextDec, connectors, groups, cards);
      }
      return;
    }

    // 2. Connector preview drawing
    if (activeConnectorSource) {
      setConnectorMousePos(canvasPt);
      // Find hovered node dynamically using elementFromPoint since pointer capture blocks mouseenter
      const elementUnderPointer = document.elementFromPoint(e.clientX, e.clientY);
      const targetElementWrapper = elementUnderPointer?.closest('[data-element-id]');
      const targetIdAttr = targetElementWrapper?.getAttribute('data-element-id');
      if (targetIdAttr && targetIdAttr !== activeConnectorSource.id) {
        setHoveredNodeId(targetIdAttr);
      } else {
        setHoveredNodeId(null);
      }
      return;
    }

    // 3. Selection Box marquee drag sizing
    if (marqueeStart && marqueeEnd) {
      setMarqueeEnd(canvasPt);
      
      // Compute overlapping nodes
      const mIds: string[] = [];
      const mStart = marqueeStart;
      const mEnd = canvasPt;

      // Check cards
      cards.forEach(c => {
        if (!c.isDeleted && (selectedNotebookId ? c.notebookId === selectedNotebookId : true)) {
          if (c.boardX !== undefined && c.boardY !== undefined) {
            if (isElementInMarquee(c.boardX, c.boardY, c.boardWidth || 200, c.boardHeight || 180, mStart, mEnd)) {
              mIds.push(c.id);
            }
          }
        }
      });

      // Check decorators
      decorations.forEach(d => {
        if (isElementInMarquee(d.x, d.y, d.width, d.height, mStart, mEnd)) {
          mIds.push(d.id);
        }
      });

      // Check groups
      groups.forEach(g => {
        if (isElementInMarquee(g.x, g.y, g.width, g.height, mStart, mEnd)) {
          mIds.push(g.id);
        }
      });

      setSelectedIds(mIds);
      return;
    }

    // 4. Element resizing handle drag
    if (isResizingId && resizingHandle && dragStart) {
      const activeEl = findElementById(isResizingId);
      if (!activeEl) return;

      const isCard = activeEl.boardX !== undefined;
      const ox = isCard ? activeEl.boardX : activeEl.x;
      const oy = isCard ? activeEl.boardY : activeEl.y;
      const ow = isCard ? activeEl.boardWidth : activeEl.width;
      const oh = isCard ? activeEl.boardHeight : activeEl.height;

      let newX = ox;
      let newY = oy;
      let newW = ow;
      let newH = oh;

      if (resizingHandle === 'br') {
        newW = Math.max(120, canvasPt.x - ox);
        newH = Math.max(100, canvasPt.y - oy);
      } else if (resizingHandle === 'bl') {
        const nextX = Math.min(ox + ow - 120, canvasPt.x);
        newW = ox + ow - nextX;
        newX = nextX;
        newH = Math.max(100, canvasPt.y - oy);
      } else if (resizingHandle === 'tr') {
        newW = Math.max(120, canvasPt.x - ox);
        const nextY = Math.min(oy + oh - 100, canvasPt.y);
        newH = oy + oh - nextY;
        newY = nextY;
      } else if (resizingHandle === 'tl') {
        const nextX = Math.min(ox + ow - 120, canvasPt.x);
        newW = ox + ow - nextX;
        newX = nextX;
        const nextY = Math.min(oy + oh - 100, canvasPt.y);
        newH = oy + oh - nextY;
        newY = nextY;
      }

      if (isCard) {
        setCards(cards.map(c => c.id === isResizingId ? {
          ...c,
          boardX: newX,
          boardY: newY,
          boardWidth: newW,
          boardHeight: newH
        } : c));
      } else if (activeEl.type === 'group') {
        const origGroup = initialResizingGroupRef.current;
        const origChildren = initialResizingChildrenRef.current;

        if (origGroup && origGroup.width > 0 && origGroup.height > 0) {
          const scaleX = newW / origGroup.width;
          const scaleY = newH / origGroup.height;

          setGroups(groups.map(g => g.id === isResizingId ? {
            ...g,
            x: newX,
            y: newY,
            width: newW,
            height: newH
          } : g));

          setCards(cards.map(c => {
            const orig = origChildren[c.id];
            if (orig) {
              const relX = orig.x - origGroup.x;
              const relY = orig.y - origGroup.y;
              
              const newChildX = newX + relX * scaleX;
              const newChildY = newY + relY * scaleY;
              const newChildW = Math.max(60, orig.width * scaleX);
              const newChildH = Math.max(50, orig.height * scaleY);
              
              const updatedCard: NoteCard = {
                ...c,
                boardX: newChildX,
                boardY: newChildY,
                boardWidth: newChildW,
                boardHeight: newChildH
              };

              const baseFontSize = orig.fontSize || 12;
              const newFontSize = Math.round(Math.max(8, Math.min(36, baseFontSize * scaleX)));
              updatedCard.fontSize = newFontSize;

              return updatedCard;
            }
            return c;
          }));

          setDecorations(decorations.map(d => {
            const orig = origChildren[d.id];
            if (orig) {
              const relX = orig.x - origGroup.x;
              const relY = orig.y - origGroup.y;
              
              const newChildX = newX + relX * scaleX;
              const newChildY = newY + relY * scaleY;
              const newChildW = Math.max(20, orig.width * scaleX);
              const newChildH = Math.max(20, orig.height * scaleY);

              let scaledPoints = d.points;
              if (d.type === 'path' && d.points) {
                scaledPoints = d.points.map(pt => {
                  const relPtX = pt.x - origGroup.x;
                  const relPtY = pt.y - origGroup.y;
                  return {
                    x: newX + relPtX * scaleX,
                    y: newY + relPtY * scaleY
                  };
                });
              }

              const updatedDec: BoardDecoration = {
                ...d,
                x: newChildX,
                y: newChildY,
                width: newChildW,
                height: newChildH,
                points: scaledPoints
              };

              if (d.type === 'text' || d.type === 'shape') {
                const baseFontSize = orig.fontSize || 12;
                const newFontSize = Math.round(Math.max(8, Math.min(36, baseFontSize * scaleX)));
                updatedDec.fontSize = newFontSize;
              }

              return updatedDec;
            }
            return d;
          }));
        } else {
          setGroups(groups.map(g => g.id === isResizingId ? {
            ...g,
            x: newX,
            y: newY,
            width: newW,
            height: newH
          } : g));
        }
      } else {
        setDecorations(decorations.map(d => d.id === isResizingId ? {
          ...d,
          x: newX,
          y: newY,
          width: newW,
          height: newH
        } : d));
      }
      return;
    }

    // 5. Element Dragging & Offset updates
    if (dragStart && selectedIds.length > 0) {
      // Update dragging status classes for animations
      if (draggingIds.length === 0) {
        setDraggingIds(Object.keys(dragOffsets));
      }

      // Apply drag update
      const updatedCards = cards.map(c => {
        const offset = dragOffsets[c.id];
        if (offset) {
          if (c.isPinned) return c; // pinned items do not move
          return {
            ...c,
            boardX: canvasPt.x - offset.x,
            boardY: canvasPt.y - offset.y,
          };
        }
        return c;
      });

      const updatedDec = decorations.map(d => {
        const offset = dragOffsets[d.id];
        if (offset) {
          return {
            ...d,
            x: canvasPt.x - offset.x,
            y: canvasPt.y - offset.y,
          };
        }
        return d;
      });

      const updatedGroups = groups.map(g => {
        const offset = dragOffsets[g.id];
        if (offset) {
          return {
            ...g,
            x: canvasPt.x - offset.x,
            y: canvasPt.y - offset.y,
          };
        }
        return g;
      });

      setCards(updatedCards);
      setDecorations(updatedDec);
      setGroups(updatedGroups);
      return;
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    workspaceRef.current?.releasePointerCapture(e.pointerId);

    // Commit drawing path
    if (isDrawing) {
      setIsDrawing(false);
      if (currentPathPoints.length > 1) {
        pushToHistory();
        const newDrawing: BoardDecoration = {
          id: `draw-${Date.now()}`,
          type: 'path',
          x: Math.min(...currentPathPoints.map(p => p.x)),
          y: Math.min(...currentPathPoints.map(p => p.y)),
          width: Math.max(...currentPathPoints.map(p => p.x)) - Math.min(...currentPathPoints.map(p => p.x)),
          height: Math.max(...currentPathPoints.map(p => p.y)) - Math.min(...currentPathPoints.map(p => p.y)),
          points: currentPathPoints,
          color: activeColor,
          lineWidth: brushWidth,
          opacity: activeTool === 'highlighter' ? 0.45 : 1,
        };
        const nextDec = [...decorations, newDrawing];
        setDecorations(nextDec);
        saveCanvasState(nextDec, connectors, groups);
      }
      setCurrentPathPoints([]);
      return;
    }

    // Stop Panning
    if (isPanning) {
      setIsPanning(false);
      return;
    }

    // Connector Line Creation commit
    if (activeConnectorSource && connectorMousePos) {
      // Find what element is hovered or under pointer
      let targetNodeId: string | null = null;
      let targetEdge: 'top' | 'right' | 'bottom' | 'left' = 'top';
      
      const elementUnderPointer = document.elementFromPoint(e.clientX, e.clientY);
      const targetElementWrapper = elementUnderPointer?.closest('[data-element-id]');
      const targetIdAttr = targetElementWrapper?.getAttribute('data-element-id');
      
      if (targetIdAttr && targetIdAttr !== activeConnectorSource.id) {
        targetNodeId = targetIdAttr;
      }

      const targetNode = targetNodeId ? findElementById(targetNodeId) : null;
      if (targetNode) {
        // Determine closest edge to pointer
        const tx = targetNode.boardX !== undefined ? targetNode.boardX : targetNode.x;
        const ty = targetNode.boardY !== undefined ? targetNode.boardY : targetNode.y;
        const tw = targetNode.boardWidth !== undefined ? targetNode.boardWidth : targetNode.width;
        const th = targetNode.boardHeight !== undefined ? targetNode.boardHeight : targetNode.height;

        const distances = {
          top: Math.hypot(connectorMousePos.x - (tx + tw / 2), connectorMousePos.y - ty),
          right: Math.hypot(connectorMousePos.x - (tx + tw), connectorMousePos.y - (ty + th / 2)),
          bottom: Math.hypot(connectorMousePos.x - (tx + tw / 2), connectorMousePos.y - (ty + th)),
          left: Math.hypot(connectorMousePos.x - tx, connectorMousePos.y - (ty + th / 2)),
        };

        const sorted = Object.entries(distances).sort((a, b) => a[1] - b[1]);
        targetEdge = sorted[0][0] as any;

        pushToHistory();
        const newConn: BoardConnector = {
          id: `conn-${Date.now()}`,
          fromId: activeConnectorSource.id,
          fromEdge: activeConnectorSource.edge,
          toId: targetNodeId!,
          toEdge: targetEdge,
          color: activeColor,
        };
        const nextConn = [...connectors, newConn];
        setConnectors(nextConn);
        saveCanvasState(decorations, nextConn, groups);
      }

      setActiveConnectorSource(null);
      setConnectorMousePos(null);
      setHoveredNodeId(null);
      return;
    }

    // Stop Marquee Select Commit
    if (marqueeStart && marqueeEnd) {
      setMarqueeStart(null);
      setMarqueeEnd(null);
      return;
    }

    // Stop Resizing
    if (isResizingId) {
      saveCanvasState(decorations, connectors, groups, cards);
      setIsResizingId(null);
      setResizingHandle(null);
      initialResizingGroupRef.current = null;
      initialResizingChildrenRef.current = {};
      return;
    }

    // Stop Dragging
    if (dragStart) {
      saveCanvasState(decorations, connectors, groups, cards);
      
      // Trigger drop bounce animation class
      if (draggingIds.length === 1) {
        setJustDroppedId(draggingIds[0]);
        setTimeout(() => setJustDroppedId(null), 350);
      }

      setDragStart(null);
      setDragOffsets({});
      setDraggedGroupId(null);
      setDraggingIds([]);
      return;
    }
  };

    // Click handler on notes/shapes to set selections
  const handleElementDown = (e: React.PointerEvent, id: string) => {
    if (activeTool === 'pen' || activeTool === 'highlighter') {
      return;
    }
    if (activeTool === 'arrow') {
      // Let it bubble up to handlePointerDown on the viewport for connection drawing
      return;
    }
    if (activeTool === 'eraser') {
      // Let it bubble up to handlePointerDown on viewport for mathematical point erasing
      return;
    }
    if (editingTextId === id || editingGroupId === id) {
      // Prevent dragging and pointer capture while editing/typing
      e.stopPropagation();
      return;
    }
    e.stopPropagation();

    const clickedEl = findElementById(id);
    if (clickedEl && clickedEl.type === 'comment') {
      setActiveCommentId(id);
    }

    // Manual double click detection to bypass pointer capture interference
    const now = Date.now();
    const isDoubleClick = lastClickRef.current && 
                          lastClickRef.current.id === id && 
                          (now - lastClickRef.current.time) < 650;
    
    lastClickRef.current = { id, time: now };

    if (isDoubleClick) {
      const element = findElementById(id);
      if (element) {
        setActiveTool('select'); // Automatically switch to select tool on double-click
        // Reset dragging states
        setDragStart(null);
        setDragOffsets({});
        setDraggedGroupId(null);
        setDraggingIds([]);
        if (workspaceRef.current && workspaceRef.current.hasPointerCapture(e.pointerId)) {
          workspaceRef.current.releasePointerCapture(e.pointerId);
        }

        if (element.type === 'sticky') {
          const card = cards.find(c => c.id === id);
          if (card) {
            setEditingCard(card);
          }
        } else if (element.type === 'text' || element.type === 'shape') {
          setEditingTextId(id);
          setTextEditValue(element.text || '');
        } else if (element.type === 'group') {
          setEditingGroupId(id);
          setTitleEditValue(element.title || '');
        }
        return;
      }
    }

    // Select Tool Drag Ignition
    if (activeTool === 'select') {
      // If the editor is open and the clicked element is a card, switch editor focus immediately on single click
      const clickedEl = findElementById(id);
      if (clickedEl?.type === 'sticky' && editingCard) {
        const card = cards.find(c => c.id === id);
        if (card) {
          setEditingCard(card);
        }
      }

      // Redirect selection and dragging of grouped cards/decorations to their group container
      let targetId = id;
      if (clickedEl && clickedEl.type !== 'group') {
        const cx = clickedEl.boardX !== undefined ? clickedEl.boardX : clickedEl.x;
        const cy = clickedEl.boardY !== undefined ? clickedEl.boardY : clickedEl.y;
        const containingGroup = groups.find(g => (
          cx >= g.x &&
          cx <= g.x + g.width &&
          cy >= g.y &&
          cy <= g.y + g.height
        ));
        if (containingGroup) {
          targetId = containingGroup.id;
        }
      }

      let nextSelected = [...selectedIds];
      if (e.ctrlKey || e.metaKey || e.shiftKey) {
        // Toggle selection
        if (selectedIds.includes(targetId)) {
          nextSelected = selectedIds.filter(val => val !== targetId);
        } else {
          nextSelected.push(targetId);
        }
      } else {
        // Normal select click sets single target unless clicking inside already selected multiple set
        if (!selectedIds.includes(targetId)) {
          nextSelected = [targetId];
        }
      }

      setSelectedIds(nextSelected);

      const canvasPt = screenToCanvas(e.clientX, e.clientY);
      setDragStart(canvasPt);

      // Save initial offsets for all elements in selection (including all elements inside selected groups)
      const offsets: Record<string, Point> = {};
      const allTargetIds = new Set<string>(nextSelected);

      nextSelected.forEach(elId => {
        const group = groups.find(g => g.id === elId);
        if (group) {
          // Find all cards inside this group container boundary
          cards.forEach(c => {
            if (!c.isDeleted && c.boardX !== undefined && c.boardY !== undefined) {
              if (
                c.boardX >= group.x &&
                c.boardX <= group.x + group.width &&
                c.boardY >= group.y &&
                c.boardY <= group.y + group.height
              ) {
                allTargetIds.add(c.id);
              }
            }
          });
          
          // Find all decorators inside this group boundary
          decorations.forEach(d => {
            if (
              d.x >= group.x &&
              d.x <= group.x + group.width &&
              d.y >= group.y &&
              d.y <= group.y + group.height
            ) {
              allTargetIds.add(d.id);
            }
          });
        }
      });

      allTargetIds.forEach(elId => {
        const item = findElementById(elId);
        if (item) {
          const itemX = item.boardX !== undefined ? item.boardX : item.x;
          const itemY = item.boardY !== undefined ? item.boardY : item.y;
          offsets[elId] = {
            x: canvasPt.x - itemX,
            y: canvasPt.y - itemY,
          };
        }
      });
      setDragOffsets(offsets);
      
      const element = findElementById(targetId);
      if (element?.type === 'group') {
        setDraggedGroupId(targetId);
      }
      
      workspaceRef.current?.setPointerCapture(e.pointerId);
    }
  };

  // Double Click edits trigger
  const handleElementDoubleClick = (e: React.MouseEvent, id: string, type: 'card' | 'decoration' | 'group') => {
    e.stopPropagation();
    setActiveTool('select'); // Automatically switch to select tool on double-click

    if (type === 'card') {
      const card = cards.find(c => c.id === id);
      if (card) {
        setEditingCard(card);
      }
      return;
    }

    if (type === 'decoration') {
      const dec = decorations.find(d => d.id === id);
      if (dec?.type === 'text' || dec?.type === 'shape') {
        setEditingTextId(id);
        setTextEditValue(dec.text || '');
      }
    } else if (type === 'group') {
      setEditingGroupId(id);
      const grp = groups.find(g => g.id === id);
      setTitleEditValue(grp?.title || '');
    }
  };

  // Save Text / Titles inline modifications
  const handleSaveInlineText = () => {
    if (!editingTextId) return;

    pushToHistory();
    // Check if it is a card
    const isCard = cards.some(c => c.id === editingTextId);
    if (isCard) {
      const updatedCards = cards.map(c => c.id === editingTextId ? {
        ...c,
        textContent: textEditValue,
        title: textEditValue.slice(0, 32) || 'Sticky Note',
        updatedAt: Date.now()
      } : c);
      setCards(updatedCards);
      saveCanvasState(decorations, connectors, groups, updatedCards);
    } else {
      const updatedDec = decorations.map(d => d.id === editingTextId ? { ...d, text: textEditValue } : d);
      setDecorations(updatedDec);
      saveCanvasState(updatedDec, connectors, groups);
    }

    setEditingTextId(null);
  };

  const handleSaveInlineGroupTitle = () => {
    if (!editingGroupId) return;
    pushToHistory();
    const updatedGroups = groups.map(g => g.id === editingGroupId ? { ...g, title: titleEditValue || 'Group Container' } : g);
    setGroups(updatedGroups);
    saveCanvasState(decorations, connectors, updatedGroups);
    setEditingGroupId(null);
  };

  const handleAddCommentToThread = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCommentText.trim() || !activeCommentId) return;

    pushToHistory();
    const updated = decorations.map((el) => {
      if (el.id === activeCommentId) {
        const commentsList = el.comments || [];
        return {
          ...el,
          comments: [
            ...commentsList,
            {
              id: `subC-${Date.now()}`,
              author: newCommentAuthor.trim() || 'Guest Reviewer',
              text: newCommentText.trim(),
              timestamp: Date.now(),
            }
          ]
        };
      }
      return el;
    });

    setDecorations(updated);
    saveCanvasState(updated, connectors, groups);
    setNewCommentText('');
  };

  // Color picker selection updater for selected nodes
  const handleColorPresetChange = (color: string) => {
    if (selectedIds.length === 0) return;
    pushToHistory();

    const pastelMatch = PASTEL_COLORS.find(p => p.fill.toLowerCase() === color.toLowerCase() || p.border.toLowerCase() === color.toLowerCase());

    const updatedCards = cards.map(c => {
      if (selectedIds.includes(c.id)) {
        return {
          ...c,
          boardColor: color,
          // Sync border/text if matched pastel preset
          colorClass: (pastelMatch ? `border-[${pastelMatch.border}]` : c.colorClass) as any
        };
      }
      return c;
    });

    const updatedDec = decorations.map(d => {
      if (selectedIds.includes(d.id)) {
        return {
          ...d,
          color: color,
          fillColor: d.type === 'shape' ? color + '15' : undefined
        };
      }
      return d;
    });

    const updatedGroups = groups.map(g => {
      if (selectedIds.includes(g.id)) {
        return { ...g, color: color };
      }
      return g;
    });

    setCards(updatedCards);
    setDecorations(updatedDec);
    setGroups(updatedGroups);
    saveCanvasState(updatedDec, connectors, updatedGroups, updatedCards);
  };

  // Drag handles for connector arrows creation
  const handleConnectorPointerDown = (e: React.PointerEvent, nodeId: string, edge: 'top' | 'right' | 'bottom' | 'left') => {
    e.stopPropagation();
    e.preventDefault();
    const canvasPt = screenToCanvas(e.clientX, e.clientY);

    setActiveConnectorSource({ id: nodeId, edge });
    setConnectorMousePos(canvasPt);
    workspaceRef.current?.setPointerCapture(e.pointerId);
  };

  const handleCreateGroupFrame = () => {
    if (selectedIds.length < 2) return;
    pushToHistory();

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    selectedIds.forEach(id => {
      const el = findElementById(id);
      if (el) {
        const x = el.boardX !== undefined ? el.boardX : el.x;
        const y = el.boardY !== undefined ? el.boardY : el.y;
        const w = el.boardWidth !== undefined ? el.boardWidth : el.width;
        const h = el.boardHeight !== undefined ? el.boardHeight : el.height;

        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x + w > maxX) maxX = x + w;
        if (y + h > maxY) maxY = y + h;
      }
    });

    // ── PERFECT GRID ALIGNMENT FOR STICKY NOTE CARDS ──
    const selectedCards = cards.filter(c => selectedIds.includes(c.id));
    let finalMaxX = maxX;
    let finalMaxY = maxY;
    let finalCards = cards;

    if (selectedCards.length > 0) {
      // Sort selected cards by current position (top-to-bottom, left-to-right)
      const sortedCards = [...selectedCards].sort((a, b) => {
        // Group cards in rows of 60px height threshold
        const rowThreshold = 60;
        const rowDiff = Math.round(a.boardY / rowThreshold) - Math.round(b.boardY / rowThreshold);
        if (rowDiff !== 0) {
          return rowDiff;
        }
        return a.boardX - b.boardX;
      });

      const gap = 16;
      const cardW = 200;
      const cardH = 180;
      const N = sortedCards.length;

      // Determine appropriate column count based on grid size
      let cols = 3;
      if (N <= 3) cols = N;
      else if (N === 4) cols = 2;
      else if (N <= 6) cols = 3;
      else if (N <= 9) cols = 3;
      else if (N <= 12) cols = 4;
      else if (N <= 16) cols = 4;
      else if (N <= 20) cols = 5;
      else cols = 6;

      const rows = Math.ceil(N / cols);

      finalCards = cards.map(c => {
        const sortedIndex = sortedCards.findIndex(sc => sc.id === c.id);
        if (sortedIndex !== -1) {
          const col = sortedIndex % cols;
          const row = Math.floor(sortedIndex / cols);
          
          return {
            ...c,
            boardX: minX + col * (cardW + gap),
            boardY: minY + row * (cardH + gap),
            boardWidth: cardW,
            boardHeight: cardH
          };
        }
        return c;
      });

      // Update state
      setCards(finalCards);

      // Recalculate aligned bounding rectangle dimensions
      finalMaxX = minX + cols * cardW + (cols - 1) * gap;
      finalMaxY = minY + rows * cardH + (rows - 1) * gap;
    }

    const padding = 24;
    const headerHeight = 44;
    const groupX = minX - padding;
    const groupY = minY - headerHeight - padding;
    const groupWidth = (finalMaxX - minX) + (padding * 2);
    const groupHeight = (finalMaxY - minY) + headerHeight + (padding * 2);

    const newGroupId = `group-${Date.now()}`;
    const newGroup: BoardGroup = {
      id: newGroupId,
      title: 'Brainstorming Group',
      x: groupX,
      y: groupY,
      width: groupWidth,
      height: groupHeight,
      color: '#6366f1',
    };

    const nextGroups = [...groups, newGroup];
    setGroups(nextGroups);
    setSelectedIds([newGroupId]);
    saveCanvasState(decorations, connectors, nextGroups, finalCards);
  };

  const togglePinState = (cardId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    pushToHistory();
    const updated = cards.map(c => c.id === cardId ? { ...c, isPinned: !c.isPinned } : c);
    setCards(updated);
    saveCanvasState(decorations, connectors, groups, updated);
  };

  // Image Upload helper
  const handleImageUploaded = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        pushToHistory();
        const center = screenToCanvas(window.innerWidth / 2, window.innerHeight / 2);
        const newImg: BoardDecoration = {
          id: `img-${Date.now()}`,
          type: 'image',
          x: center.x - 150,
          y: center.y - 110,
          width: 300,
          height: 220,
          color: '#e2e8f0',
          dataUrl: event.target.result as string,
        };
        const nextDec = [...decorations, newImg];
        setDecorations(nextDec);
        setSelectedIds([newImg.id]);
        saveCanvasState(nextDec, connectors, groups);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = ''; // Reset
  };

  // Helper calculation to draw smooth bezier arrow connectors paths
  const drawConnectorCurve = (conn: BoardConnector) => {
    const fromCoords = getNodeEdgeCoords(conn.fromId, conn.fromEdge);
    const toCoords = getNodeEdgeCoords(conn.toId, conn.toEdge);
    if (!fromCoords || !toCoords) return '';

    const dx = Math.abs(toCoords.x - fromCoords.x) * 0.5;
    const dy = Math.abs(toCoords.y - fromCoords.y) * 0.5;

    let cx1 = fromCoords.x;
    let cy1 = fromCoords.y;
    let cx2 = toCoords.x;
    let cy2 = toCoords.y;

    // Extend bezier controls outward depending on edge face directions
    if (conn.fromEdge === 'left') cx1 -= dx;
    else if (conn.fromEdge === 'right') cx1 += dx;
    else if (conn.fromEdge === 'top') cy1 -= dy;
    else if (conn.fromEdge === 'bottom') cy1 += dy;

    if (conn.toEdge === 'left') cx2 -= dx;
    else if (conn.toEdge === 'right') cx2 += dx;
    else if (conn.toEdge === 'top') cy2 -= dy;
    else if (conn.toEdge === 'bottom') cy2 += dy;

    return `M ${fromCoords.x} ${fromCoords.y} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${toCoords.x} ${toCoords.y}`;
  };

  // Filter notes cards matching active search keyword
  const filteredCards = cards.filter(c => {
    if (c.isDeleted) return false;
    if (selectedNotebookId && c.notebookId !== selectedNotebookId) return false;
    return true;
  });

  const getFilteredNodes = () => {
    const nodes: Array<{
      id: string;
      x: number;
      y: number;
      width: number;
      height: number;
      fillColor?: string;
      color?: string;
    }> = [];

    filteredCards.forEach(c => {
      if (c.boardX !== undefined && c.boardY !== undefined) {
        nodes.push({
          id: c.id,
          x: c.boardX,
          y: c.boardY,
          width: c.boardWidth || 200,
          height: c.boardHeight || 180,
          fillColor: c.boardColor || '#FFF4B8',
          color: '#ca8a04',
        });
      }
    });

    decorations.forEach(d => {
      nodes.push({
        id: d.id,
        x: d.x,
        y: d.y,
        width: d.width,
        height: d.height,
        fillColor: d.fillColor || 'rgba(0,0,0,0.02)',
        color: d.color || '#94a3b8',
      });
    });

    groups.forEach(g => {
      nodes.push({
        id: g.id,
        x: g.x,
        y: g.y,
        width: g.width,
        height: g.height,
        fillColor: 'rgba(99, 102, 241, 0.03)',
        color: g.color || '#6366f1',
      });
    });

    return nodes;
  };

  // Color mapper preset selector triggered from canvas element click
  const mapSelectedElementForToolbar = (): WhiteboardElement | undefined => {
    if (selectedIds.length !== 1) return undefined;
    const el = findElementById(selectedIds[0]);
    if (!el) return undefined;

    return {
      id: el.id,
      type: el.boardX !== undefined ? 'sticky' : el.type,
      x: el.boardX !== undefined ? el.boardX : el.x,
      y: el.boardY !== undefined ? el.boardY : el.y,
      width: el.boardWidth !== undefined ? el.boardWidth : el.width,
      height: el.boardHeight !== undefined ? el.boardHeight : el.height,
      color: el.boardColor || el.color || '#3b82f6',
      fillColor: el.boardColor || el.fillColor || '#FFF4B8',
      lineWidth: el.lineWidth || 2,
      opacity: el.opacity || 1,
      text: el.text || el.textContent || '',
      fontFamily: el.fontFamily,
      fontSize: el.fontSize,
      fontWeight: el.fontWeight,
      fontStyle: el.fontStyle,
      textDecoration: el.textDecoration,
      textAlign: el.textAlign,
      lineHeight: el.lineHeight,
    };
  };

  const handleUpdateElementFromToolbar = (id: string, updates: Partial<WhiteboardElement>) => {
    pushToHistory();
    const isCard = cards.some(c => c.id === id);
    if (isCard) {
      const updated = cards.map(c => {
        if (c.id === id) {
          return {
            ...c,
            boardColor: updates.fillColor || c.boardColor,
            boardWidth: updates.width || c.boardWidth,
            boardHeight: updates.height || c.boardHeight,
            textContent: updates.text !== undefined ? updates.text : c.textContent,
            fontFamily: updates.fontFamily !== undefined ? updates.fontFamily : c.fontFamily,
            fontSize: updates.fontSize !== undefined ? updates.fontSize : c.fontSize,
            fontWeight: updates.fontWeight !== undefined ? updates.fontWeight : c.fontWeight,
            fontStyle: updates.fontStyle !== undefined ? updates.fontStyle : c.fontStyle,
            textDecoration: updates.textDecoration !== undefined ? updates.textDecoration : c.textDecoration,
            textAlign: updates.textAlign !== undefined ? updates.textAlign : c.textAlign,
            lineHeight: updates.lineHeight !== undefined ? updates.lineHeight : c.lineHeight,
          };
        }
        return c;
      });
      setCards(updated);
      saveCanvasState(decorations, connectors, groups, updated);
    } else {
      const updatedDec = decorations.map(d => {
        if (d.id === id) {
          return {
            ...d,
            fillColor: updates.fillColor || d.fillColor,
            color: updates.color || d.color,
            width: updates.width || d.width,
            height: updates.height || d.height,
            text: updates.text !== undefined ? updates.text : d.text,
            fontFamily: updates.fontFamily !== undefined ? updates.fontFamily : d.fontFamily,
            fontSize: updates.fontSize !== undefined ? updates.fontSize : d.fontSize,
            fontWeight: updates.fontWeight !== undefined ? updates.fontWeight : d.fontWeight,
            fontStyle: updates.fontStyle !== undefined ? updates.fontStyle : d.fontStyle,
            textDecoration: updates.textDecoration !== undefined ? updates.textDecoration : d.textDecoration,
            textAlign: updates.textAlign !== undefined ? updates.textAlign : d.textAlign,
            lineHeight: updates.lineHeight !== undefined ? updates.lineHeight : d.lineHeight,
          };
        }
        return d;
      });
      setDecorations(updatedDec);
      saveCanvasState(updatedDec, connectors, groups);
    }
  };

  const selectedElementToolbar = mapSelectedElementForToolbar();

  return (
    <div
      ref={workspaceRef}
      className={`flex-1 flex flex-col relative select-none outline-none overflow-hidden h-full w-full bg-[#f5f5f5] dark:bg-slate-950 transition-colors ${
        spacePressed || activeTool === 'hand' 
          ? 'cursor-grab active:cursor-grabbing' 
          : ['eraser', 'pen', 'highlighter'].includes(activeTool)
            ? 'cursor-none'
            : 'cursor-default'
      }`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={() => setMouseScreenPos(null)}
      onContextMenu={handleContextMenu}
      id="sticky-board-whiteboard-viewport"
    >
      {/* ── BACKGROUND PATTERNS ── */}
      <div 
        className="absolute inset-0 pointer-events-none" 
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
          transition: isPanning ? 'none' : 'transform 100ms cubic-bezier(0.1, 0.9, 0.2, 1)'
        }}
      >
        {backgroundStyle === 'dots' && (
          <div 
            className="absolute -inset-[10000px] opacity-100"
            style={{
              backgroundImage: 'radial-gradient(rgba(148, 163, 184, 0.28) 1.5px, transparent 1.5px)',
              backgroundSize: '24px 24px'
            }}
          />
        )}
        {backgroundStyle === 'grid' && (
          <div 
            className="absolute -inset-[10000px] opacity-100"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(0, 0, 0, 0.08) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(0, 0, 0, 0.08) 1px, transparent 1px),
                linear-gradient(to right, rgba(0, 0, 0, 0.03) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(0, 0, 0, 0.03) 1px, transparent 1px)
              `,
              backgroundSize: '150px 150px, 150px 150px, 30px 30px, 30px 30px'
            }}
          />
        )}
      </div>

      {/* ── FLOATING TOOLBAR INTEGRATION ── */}
      <div
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onPointerEnter={(e) => { e.stopPropagation(); setMouseScreenPos(null); }}
      >
        <WhiteboardToolbar
          activeTool={activeTool}
          setActiveTool={setActiveTool}
          onClear={() => {
            setConfirmDialog({
              isOpen: true,
              title: 'Clear Whiteboard',
              message: 'Are you sure you want to clear the entire whiteboard? This will delete all decorations and arrow connections.',
              onConfirm: () => {
                pushToHistory();
                setDecorations([]);
                setConnectors([]);
                setGroups([]);
                saveCanvasState([], [], []);
              }
            });
          }}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={undoStack.length > 0}
          canRedo={redoStack.length > 0}
          gridVisible={gridVisible}
          setGridVisible={setGridVisible}
          sheetMode="infinite"
          onOpenSheetSettings={() => {
            // Cycle background style preset
            setBackgroundStyle(prev => {
              if (prev === 'dots') return 'grid';
              return 'dots';
            });
          }}
          activeColor={activeColor}
          setActiveColor={(color) => {
            setActiveColor(color);
            handleColorPresetChange(color);
          }}
          toolbarPosition="left"
          setToolbarPosition={() => {}}
          selectedElement={selectedElementToolbar}
          onUpdateElement={handleUpdateElementFromToolbar}
          brushWidth={brushWidth}
          setBrushWidth={setBrushWidth}
          eraserWidth={eraserWidth}
          setEraserWidth={setEraserWidth}
          brushColors={BRUSH_COLORS}
          onAddElement={(el) => {
            pushToHistory();
            // Map screen coordinates (el.x, el.y) to current canvas zoom/pan viewport center
            const screenCenter = { x: el.x + (el.width || 0) / 2, y: el.y + (el.height || 0) / 2 };
            const canvasCenter = screenToCanvas(screenCenter.x, screenCenter.y);
            const canvasX = canvasCenter.x - (el.width || 0) / 2;
            const canvasY = canvasCenter.y - (el.height || 0) / 2;

            if (el.type === 'sticky') {
              // Add card
              const pastel = PASTEL_COLORS[Math.floor(Math.random() * PASTEL_COLORS.length)];
              const newCard: NoteCard = {
                id: `card-${Date.now()}`,
                notebookId: activeNotebookId,
                type: 'text',
                title: el.title || 'Sticky Note',
                colorClass: 'bg-amber-50 dark:bg-amber-955/20 text-slate-900 border-amber-200' as any,
                createdAt: Date.now(),
                updatedAt: Date.now(),
                isFavorite: false,
                isLocked: false,
                tags: [],
                textContent: el.text || '',
                contentHtml: (el as any).contentHtml,
                boardX: canvasX,
                boardY: canvasY,
                boardWidth: el.width || 200,
                boardHeight: el.height || 180,
                boardColor: el.fillColor || pastel.fill,
              };
              const nextCards = [newCard, ...cards];
              setCards(nextCards);
              saveCanvasState(decorations, connectors, groups, nextCards);
              setSelectedIds([newCard.id]);
            } else {
              // Add decorator
              const newDec: BoardDecoration = {
                id: el.id,
                type: el.type === 'path' ? 'path' : el.type === 'shape' ? 'shape' : 'text',
                x: canvasX,
                y: canvasY,
                width: el.width,
                height: el.height,
                color: el.color,
                fillColor: el.fillColor,
                text: el.text,
                shapeType: el.shapeType,
                points: el.points,
              };
              const nextDec = [...decorations, newDec];
              setDecorations(nextDec);
              saveCanvasState(nextDec, connectors, groups);
              setSelectedIds([newDec.id]);
            }
          }}
        />
      </div>

      {/* Hidden input to receive image file selection */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUploaded}
        className="hidden"
      />

      {/* ── TOP HEADER HUD BAR ── */}
      <div className="absolute top-4 left-16 right-4 flex items-center justify-between pointer-events-none z-10">
        {/* Left: Board title edit */}
        <div 
          className="flex items-center gap-2 bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 rounded-full px-4.5 py-1.5 shadow-md pointer-events-auto"
          onPointerEnter={() => setMouseScreenPos(null)}
        >
          <Compass className="w-4 h-4 text-indigo-500 shrink-0" />
          <span className="text-xs font-bold text-slate-800 dark:text-zinc-300 select-text font-sans">
            {notebooks.find(n => n.id === activeNotebookId)?.title || 'All Notes Board'}
          </span>
          <span className="w-px h-3 bg-slate-200 dark:bg-slate-800 mx-1.5" />
          <span className="text-[10px] text-slate-400 font-mono">
            {filteredCards.length} sticky note{filteredCards.length === 1 ? '' : 's'}
          </span>
        </div>

        {/* Right: Board controls, Search keyword input & Reset View */}
        <div 
          className="flex items-center gap-2 pointer-events-auto"
          onPointerEnter={() => setMouseScreenPos(null)}
        >
          <div className="relative flex items-center bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 rounded-full px-3 py-1 shadow-md w-44 md:w-56 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
            <Search className="w-3.5 h-3.5 text-slate-400 mr-2 shrink-0" />
            <input
              type="text"
              placeholder="Search sticky canvas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-xs w-full text-slate-800 dark:text-white placeholder-slate-400/80 font-sans select-text"
              style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="p-0.5 hover:bg-slate-100 dark:hover:bg-slate-850 rounded-full text-slate-400 cursor-pointer"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Undo/Redo HUD Controls */}
          <div className="flex items-center gap-0.5 bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 rounded-full p-0.5 shadow-md">
            <button
              onClick={handleUndo}
              disabled={undoStack.length === 0}
              className={`w-7.5 h-7.5 rounded-full flex items-center justify-center transition-all ${
                undoStack.length > 0
                  ? 'text-slate-755 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer active:scale-90'
                  : 'text-slate-300 dark:text-slate-700 cursor-not-allowed opacity-50'
              }`}
              title="Undo last action (Ctrl+Z)"
            >
              <Undo2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={handleRedo}
              disabled={redoStack.length === 0}
              className={`w-7.5 h-7.5 rounded-full flex items-center justify-center transition-all ${
                redoStack.length > 0
                  ? 'text-slate-755 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer active:scale-90'
                  : 'text-slate-300 dark:text-slate-700 cursor-not-allowed opacity-50'
              }`}
              title="Redo action (Ctrl+Shift+Z or Ctrl+Y)"
            >
              <Redo2 className="w-3.5 h-3.5" />
            </button>
          </div>

          <button
            onClick={() => {
              setZoom(0.85);
              setPan({ x: 150, y: 80 });
            }}
            className="h-8.5 px-3 bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 rounded-full text-xs font-bold text-slate-655 dark:text-zinc-350 hover:bg-slate-55 dark:hover:bg-slate-850 cursor-pointer flex items-center gap-1.5 shadow-md active:scale-95 transition-transform"
            title="Recenter whiteboard zoom & pan"
          >
            <Maximize className="w-3.5 h-3.5" />
            <span>Recenter</span>
          </button>

          <button
            onClick={handleCreateGroupFrame}
            disabled={selectedIds.length < 2}
            className={`h-8.5 px-3 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-md transition-all ${
              selectedIds.length >= 2
                ? 'bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer active:scale-95 transition-transform'
                : 'bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-zinc-650 cursor-not-allowed opacity-55 shadow-none'
            }`}
            title={selectedIds.length >= 2 ? "Group selected elements together" : "Select 2 or more elements to group"}
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Add Group</span>
          </button>

          <button
            onClick={onClose}
            className="h-8.5 w-8.5 bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 rounded-full flex items-center justify-center text-slate-500 hover:text-rose-600 cursor-pointer shadow-md active:scale-95 transition-transform"
            title="Close whiteboard workspace"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── WHITEBOARD VIEWPORT CANVAS CONTAINER ── */}
      <div 
        className="flex-1 relative canvas-container select-none touch-none"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
          transition: isPanning ? 'none' : 'transform 100ms cubic-bezier(0.1, 0.9, 0.2, 1)'
        }}
      >
        {groups.map(g => {
          const isSelected = selectedIds.includes(g.id);
          const isEditing = editingGroupId === g.id;
          const isDragging = draggingIds.includes(g.id);

          return (
            <div
              key={g.id}
              data-element-id={g.id}
              className={`absolute border-2 rounded-2xl p-4 flex flex-col bg-indigo-50/5/30 ${
                isDragging || dragOffsets[g.id] || isResizingId === g.id ? "" : "transition-all"
              } ${
                isSelected ? 'border-indigo-650 ring-2 ring-indigo-300' : 'border-slate-200 border-dashed'
              }`}
              style={{
                left: g.x,
                top: g.y,
                width: g.width,
                height: g.height,
              }}
              onPointerDown={(e) => handleElementDown(e, g.id)}
              onDoubleClick={(e) => handleElementDoubleClick(e, g.id, 'group')}
            >
              {/* Group Title Header */}
              <div 
                className="flex items-center justify-between pb-2 mb-2 border-b border-slate-200 select-none cursor-move"
                onDoubleClick={(e) => handleElementDoubleClick(e, g.id, 'group')}
              >
                {isEditing ? (
                  <input
                    type="text"
                    value={titleEditValue}
                    onChange={(e) => setTitleEditValue(e.target.value)}
                    onBlur={handleSaveInlineGroupTitle}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveInlineGroupTitle()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs font-bold text-slate-800 bg-slate-100 border border-indigo-400 rounded-md px-1.5 py-0.5 outline-none font-sans select-text"
                    style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
                    autoFocus
                  />
                ) : (
                  <span 
                    className="text-xs font-bold text-slate-650 dark:text-zinc-400 capitalize font-sans flex items-center gap-1 cursor-pointer hover:text-indigo-600 transition-colors animate-fade-in"
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingGroupId(g.id);
                      setTitleEditValue(g.title || 'Brainstorming Group');
                    }}
                    title="Click to edit group name"
                  >
                    📁 {g.title}
                    <Edit2 className="w-2.5 h-2.5 opacity-60 hover:opacity-100 hover:scale-110 transition-all ml-0.5" />
                  </span>
                )}
                <span className="text-[9.5px] font-mono text-slate-400">Container</span>
              </div>

              {/* Bounding Resize handles */}
              {isSelected && (
                <div 
                  className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize flex items-center justify-center"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    setIsResizingId(g.id);
                    setResizingHandle('br');
                    const canvasPt = screenToCanvas(e.clientX, e.clientY);
                    setDragStart(canvasPt);
                    startResizingGroup(g.id);
                  }}
                >
                  <Sliders className="w-3 h-3 text-indigo-600 rotate-45" />
                </div>
              )}
            </div>
          );
        })}

        {/* Draw Shapes, Images and Freehand Paths decorations */}
        {decorations.map(d => {
          const isSelected = selectedIds.includes(d.id);
          const isEditing = editingTextId === d.id;
          const isDragging = draggingIds.includes(d.id);

          return (
            <div
              key={d.id}
              data-element-id={d.id}
              className={`absolute ${
                isDragging || dragOffsets[d.id] || isResizingId === d.id ? "" : "transition-all"
              } flex items-center justify-center p-2 text-center ${
                isEditing ? 'select-text' : 'select-none'
              } ${
                isSelected ? 'ring-2 ring-blue-500 rounded-lg' : ''
              }`}
              style={{
                left: d.x,
                top: d.y,
                width: d.width,
                height: d.height,
                cursor: activeTool === 'eraser' ? 'pointer' : 'move',
              }}
              onPointerDown={(e) => handleElementDown(e, d.id)}
              onDoubleClick={(e) => handleElementDoubleClick(e, d.id, 'decoration')}
            >
              {/* Render Shape */}
              {d.type === 'shape' && (
                <div 
                  className="absolute inset-0 transition-colors"
                  style={{
                    borderColor: d.color,
                    borderWidth: '2.5px',
                    borderStyle: 'solid',
                    backgroundColor: d.fillColor || 'transparent',
                    borderRadius: d.shapeType === 'circle' ? '50%' : d.shapeType === 'triangle' ? '0' : '8px',
                    clipPath: d.shapeType === 'triangle' ? 'polygon(50% 0%, 0% 100%, 100% 100%)' : d.shapeType === 'diamond' ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' : 'none'
                  }}
                />
              )}

              {/* Render Comment Pin */}
              {d.type === 'comment' && (
                <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-slate-900 border-2 border-blue-500 rounded-full shadow-md hover:scale-105 transition-transform">
                  <MessageSquare className="w-5 h-5 text-blue-500 fill-current" />
                </div>
              )}

              {/* Render Image */}
              {d.type === 'image' && d.dataUrl && (
                <img
                  src={d.dataUrl}
                  alt="Board upload"
                  className="w-full h-full object-cover rounded-lg border shadow-xxs pointer-events-none"
                  referrerPolicy="no-referrer"
                />
              )}

              {/* Render Inline text layer */}
              {d.type !== 'comment' && d.type !== 'image' && (
                <div className={`relative z-10 w-full h-full flex items-center justify-center px-2 ${isEditing ? 'pointer-events-auto' : 'pointer-events-none'}`}>
                  {isEditing ? (
                    <textarea
                      ref={(el) => {
                        if (el) {
                          el.focus();
                          if (el.value === 'Text Label') {
                            el.select();
                          }
                        }
                      }}
                      value={textEditValue}
                      onChange={(e) => setTextEditValue(e.target.value)}
                      onBlur={handleSaveInlineText}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSaveInlineText()}
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDoubleClick={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full bg-slate-50 border border-blue-400 rounded-md p-1 text-center text-xs outline-none font-semibold resize-none select-text"
                      style={{
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                        fontFamily: d.fontFamily || undefined,
                        fontSize: d.fontSize ? `${d.fontSize}px` : undefined,
                        fontWeight: d.fontWeight || undefined,
                        fontStyle: d.fontStyle || undefined,
                        textDecoration: d.textDecoration || undefined,
                        textAlign: (d.textAlign as any) || undefined,
                        lineHeight: d.lineHeight ? `${d.lineHeight}px` : undefined,
                      }}
                      autoFocus
                    />
                  ) : (
                    <span 
                      className="text-xs font-bold break-words block w-full text-center"
                      style={{
                        color: d.type === 'shape' ? '#1f2937' : d.color,
                        fontFamily: d.fontFamily || undefined,
                        fontSize: d.fontSize ? `${d.fontSize}px` : undefined,
                        fontWeight: d.fontWeight || undefined,
                        fontStyle: d.fontStyle || undefined,
                        textDecoration: d.textDecoration || undefined,
                        textAlign: (d.textAlign as any) || undefined,
                        lineHeight: d.lineHeight ? `${d.lineHeight}px` : undefined,
                      }}
                    >
                      {d.text}
                    </span>
                  )}
                </div>
              )}

              {/* Resize handles */}
              {isSelected && d.type !== 'comment' && (
                <div 
                  className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-blue-500 border border-white rounded-full cursor-se-resize z-20"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    setIsResizingId(d.id);
                    setResizingHandle('br');
                    setDragStart(screenToCanvas(e.clientX, e.clientY));
                  }}
                />
              )}
            </div>
          );
        })}

        {/* Draw Sticky Notes Cards */}
        {filteredCards.map(c => {
          if (c.boardX === undefined || c.boardY === undefined) return null;
          
          const isSelected = selectedIds.includes(c.id);
          const isEditing = editingTextId === c.id;
          const isDragging = draggingIds.includes(c.id);
          const isHovered = hoveredNodeId === c.id;
          const isDroppedBounce = justDroppedId === c.id;

              const isSmallCard = (c.boardHeight !== undefined && c.boardHeight < 130) || (c.boardWidth !== undefined && c.boardWidth < 130);
          const isTinyCard = (c.boardHeight !== undefined && c.boardHeight < 80) || (c.boardWidth !== undefined && c.boardWidth < 100);

          // Search highlighting opacity
          let queryMatch = true;
          if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            queryMatch = (
              c.title.toLowerCase().includes(q) ||
              (c.textContent || '').toLowerCase().includes(q) ||
              c.tags.some(t => t.toLowerCase().includes(q))
            );
          }

          return (
            <div
              key={c.id}
              data-element-id={c.id}
              className={`absolute rounded-xl border flex flex-col justify-between cursor-pointer group ${
                isDragging || dragOffsets[c.id] || isResizingId === c.id ? "" : "transition-all"
              } select-none ${
                isSelected 
                  ? 'ring-3 ring-blue-500/80 shadow-2xl scale-[1.03] z-50 border-blue-300' 
                  : 'shadow-md hover:shadow-xl hover:-translate-y-0.5 border-amber-300/40'
              } ${isDragging ? 'rotate-[1.5deg] scale-[1.04] opacity-90 z-50' : ''} ${
                isDroppedBounce ? 'animate-bounce duration-300' : ''
              }`}
              style={{
                left: c.boardX,
                top: c.boardY,
                width: c.boardWidth || 200,
                height: isEditing ? 'auto' : (c.boardHeight || 180),
                minHeight: c.boardHeight !== undefined ? undefined : '180px',
                backgroundColor: c.boardColor || '#FFF4B8',
                borderColor: isSelected ? undefined : '#ca8a04',
                opacity: queryMatch ? 1 : 0.25,
                transformOrigin: 'center center',
              }}
              onPointerDown={(e) => handleElementDown(e, c.id)}
              onDoubleClick={(e) => handleElementDoubleClick(e, c.id, 'card')}
              onMouseEnter={() => setHoveredNodeId(c.id)}
              onMouseLeave={() => setHoveredNodeId(null)}
            >
              {/* Pushpin pin icon button */}
              {c.isPinned && (
                <button 
                  onClick={(e) => { e.stopPropagation(); togglePinState(c.id, e); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="absolute -top-2 -left-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full p-1 shadow-md hover:scale-110 active:scale-95 transition-all cursor-pointer z-30 flex items-center justify-center border border-white"
                  title="Click to Unpin Card"
                >
                  <Pin className="w-3 h-3 fill-white" />
                </button>
              )}

              {/* Header tags and indications */}
              {!isTinyCard && (c.tags.length > 0 || isSelected) && (
                <div className={`${isSmallCard ? 'p-1.5 pb-0.5' : 'p-3 pb-1'} flex items-start justify-between shrink-0`}>
                  <div className="flex flex-wrap gap-1 max-w-[80%]">
                    {!isSmallCard && c.tags.map(t => (
                      <span 
                        key={t}
                        className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-black/5 text-[#5F521E] border border-black/5 uppercase tracking-wider font-mono"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  
                  {/* Actions HUD: favorite, pin, expand toggles */}
                  <div className={`flex items-center gap-1 transition-opacity ${
                    isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                  }`}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingCard(c);
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="p-1 rounded-full hover:bg-black/5 transition-colors text-slate-500 hover:text-indigo-600 cursor-pointer"
                      title="Open rich note editor popup"
                    >
                      <Maximize2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => togglePinState(c.id, e)}
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      className={`p-1 rounded-full hover:bg-black/5 transition-colors cursor-pointer ${
                        c.isPinned ? 'text-indigo-600' : 'text-slate-500'
                      }`}
                      title={c.isPinned ? 'Unpin Note' : 'Pin Note in position'}
                    >
                      <Pin className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Text content area */}
              <div className={`flex-1 flex flex-col justify-start overflow-hidden ${isTinyCard ? 'px-2 py-1' : isSmallCard ? 'px-3 py-1.5' : 'px-4.5 py-1.5'}`}>
                <>
                  <h4 
                    className="text-[13px] font-extrabold uppercase tracking-wider text-slate-850 border-b border-black/[0.05] pb-1 mb-1 font-sans truncate shrink-0" 
                    style={{ 
                      color: '#1a1301', 
                      flexShrink: 0,
                      borderBottom: isTinyCard ? 'none' : undefined,
                      marginBottom: isTinyCard ? '0px' : undefined,
                      paddingBottom: isTinyCard ? '0px' : undefined,
                      textAlign: isTinyCard ? 'center' : undefined,
                      fontSize: c.fontSize ? `${c.fontSize}px` : undefined,
                    }}
                  >
                    {c.title?.trim() || 'Untitled Note'}
                  </h4>
                  {!isTinyCard && (
                    c.contentHtml || c.textContent ? (
                      <div 
                        className="text-xs font-semibold leading-relaxed overflow-hidden w-full break-all whitespace-pre-wrap [&_p]:m-0 [&_div]:m-0 [&_ol]:m-0 [&_ul]:m-0 [&_ol]:pl-4 [&_ul]:pl-4"
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: isSmallCard ? 2 : 4,
                          WebkitBoxOrient: 'vertical',
                          textOverflow: 'ellipsis',
                          fontFamily: c.fontFamily || undefined,
                          fontSize: c.fontSize ? `${c.fontSize - 1}px` : undefined,
                          fontWeight: c.fontWeight || undefined,
                          fontStyle: c.fontStyle || undefined,
                          textDecoration: c.textDecoration || undefined,
                          textAlign: (c.textAlign as any) || undefined,
                          lineHeight: c.lineHeight ? `${c.lineHeight}px` : undefined,
                        }}
                        dangerouslySetInnerHTML={{ __html: c.contentHtml || c.textContent }}
                      />
                    ) : (
                      <p 
                        className="text-xs font-semibold leading-relaxed break-all whitespace-pre-wrap font-sans text-slate-800"
                        style={{ 
                          color: '#2d2303',
                          display: '-webkit-box',
                          WebkitLineClamp: isSmallCard ? 2 : 4,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontFamily: c.fontFamily || undefined,
                          fontSize: c.fontSize ? `${c.fontSize - 1}px` : undefined,
                          fontWeight: c.fontWeight || undefined,
                          fontStyle: c.fontStyle || undefined,
                          textDecoration: c.textDecoration || undefined,
                          textAlign: (c.textAlign as any) || undefined,
                          lineHeight: c.lineHeight ? `${c.lineHeight}px` : undefined,
                        }}
                      >
                        Double-click to expand and edit...
                      </p>
                    )
                  )}
                </>
              </div>

              {/* Bottom footer timeline tracking */}
              {!isSmallCard && (
                <div className="p-3 pt-1 border-t border-black/[0.04] text-[9px] font-mono text-slate-500 flex items-center justify-between shrink-0">
                  <span>Today</span>
                  {c.isFavorite && <Heart className="w-2.5 h-2.5 text-rose-500 fill-rose-500" />}
                </div>
              )}

              {/* Visual skeuomorphic curled paper corner flap shadow */}
              {!isSmallCard && (
                <>
                  <div 
                    className="absolute bottom-0 right-0 w-8 h-8 pointer-events-none transition-all group-hover:w-10 group-hover:h-10 z-10"
                    style={{
                      background: `linear-gradient(135deg, transparent 45%, rgba(0,0,0,0.06) 50%, rgba(255,255,255,0.2) 55%, transparent 100%)`,
                      boxShadow: `-4px -4px 6px -4px rgba(0,0,0,0.15)`
                    }}
                  />
                  <div 
                    className="absolute bottom-0 right-0 w-0 h-0 border-solid border-t-8 border-l-8 border-transparent transition-all group-hover:border-t-10 group-hover:border-l-10 z-10"
                    style={{ borderBottomColor: 'transparent', borderRightColor: 'transparent' }}
                  />
                </>
              )}

              {/* Sizing Resize handle corner */}
              {isSelected && !c.isPinned && (
                <div 
                  className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-blue-500 border border-white rounded-full cursor-se-resize z-20"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    setIsResizingId(c.id);
                    setResizingHandle('br');
                    setDragStart(screenToCanvas(e.clientX, e.clientY));
                  }}
                />
              )}

              {/* Edge handles showing on hover/select to create arrow connectors */}
              {(isHovered || isSelected) && !c.isPinned && (
                <>
                  <div
                    className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-slate-900 border-2 border-indigo-500 rounded-full hover:bg-indigo-500 cursor-alias z-20 flex items-center justify-center animate-in zoom-in duration-75 shadow-xs"
                    onPointerDown={(e) => handleConnectorPointerDown(e, c.id, 'top')}
                  />
                  <div
                    className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-white dark:bg-slate-900 border-2 border-indigo-500 rounded-full hover:bg-indigo-500 cursor-alias z-20 flex items-center justify-center animate-in zoom-in duration-75 shadow-xs"
                    onPointerDown={(e) => handleConnectorPointerDown(e, c.id, 'right')}
                  />
                  <div
                    className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-slate-900 border-2 border-indigo-500 rounded-full hover:bg-indigo-500 cursor-alias z-20 flex items-center justify-center animate-in zoom-in duration-75 shadow-xs"
                    onPointerDown={(e) => handleConnectorPointerDown(e, c.id, 'bottom')}
                  />
                  <div
                    className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-white dark:bg-slate-900 border-2 border-indigo-500 rounded-full hover:bg-indigo-500 cursor-alias z-20 flex items-center justify-center animate-in zoom-in duration-75 shadow-xs"
                    onPointerDown={(e) => handleConnectorPointerDown(e, c.id, 'left')}
                  />
                </>
              )}
            </div>
          );
        })}

        {/* ── VISUAL ARROW CONNECTOR PATHS ── */}
        <svg 
          className="absolute inset-0 pointer-events-none overflow-visible"
          id="whiteboard-svg-arrow-connectors"
        >
          <defs>
            <marker
              id="whiteboard-arrowhead-preview"
              markerWidth="8"
              markerHeight="6"
              refX="6"
              refY="3"
              orient="auto"
            >
              <polygon points="0 0, 8 3, 0 6" fill={activeColor} />
            </marker>
            {connectors.map(conn => (
              <marker
                key={`arrowhead-${conn.id}`}
                id={`arrowhead-${conn.id}`}
                markerWidth="8"
                markerHeight="6"
                refX="6"
                refY="3"
                orient="auto"
              >
                <polygon points="0 0, 8 3, 0 6" fill={conn.color || activeColor} />
              </marker>
            ))}
          </defs>

          {connectors.map(conn => {
            const pathData = drawConnectorCurve(conn);
            if (!pathData) return null;

            return (
              <g key={conn.id}>
                {/* Thick background click region line */}
                <path
                  d={pathData}
                  fill="none"
                  stroke="transparent"
                  strokeWidth={15}
                  className="pointer-events-auto cursor-pointer"
                  onPointerDown={(e) => {
                    if (activeTool === 'eraser') {
                      // Let it bubble up to handlePointerDown on viewport for mathematical point erasing
                      return;
                    }
                    e.stopPropagation();
                    setConfirmDialog({
                      isOpen: true,
                      title: 'Delete Connection',
                      message: 'Are you sure you want to delete this connection line?',
                      onConfirm: () => {
                        pushToHistory();
                        const nextConn = connectors.filter(c => c.id !== conn.id);
                        setConnectors(nextConn);
                        saveCanvasState(decorations, nextConn, groups);
                      }
                    });
                  }}
                />
                
                {/* Visual dash-offset connector path arrow */}
                <path
                  d={pathData}
                  fill="none"
                  stroke={conn.color || '#6366f1'}
                  strokeWidth={2.5}
                  strokeDasharray="6, 4"
                  className="whiteboard-connector-dash-line"
                  markerEnd={`url(#arrowhead-${conn.id})`}
                />
              </g>
            );
          })}

          {/* Active Connector Arrow drawing preview */}
          {activeConnectorSource && connectorMousePos && (() => {
            const startPt = getNodeEdgeCoords(activeConnectorSource.id, activeConnectorSource.edge);
            let pathData = `M ${startPt.x} ${startPt.y} L ${connectorMousePos.x} ${connectorMousePos.y}`;
            
            const targetNode = findElementById(hoveredNodeId || '');
            if (targetNode && targetNode.id !== activeConnectorSource.id) {
              const tx = targetNode.boardX !== undefined ? targetNode.boardX : targetNode.x;
              const ty = targetNode.boardY !== undefined ? targetNode.boardY : targetNode.y;
              const tw = targetNode.boardWidth !== undefined ? targetNode.boardWidth : targetNode.width;
              const th = targetNode.boardHeight !== undefined ? targetNode.boardHeight : targetNode.height;

              const distances = {
                top: Math.hypot(connectorMousePos.x - (tx + tw / 2), connectorMousePos.y - ty),
                right: Math.hypot(connectorMousePos.x - (tx + tw), connectorMousePos.y - (ty + th / 2)),
                bottom: Math.hypot(connectorMousePos.x - (tx + tw / 2), connectorMousePos.y - (ty + th)),
                left: Math.hypot(connectorMousePos.x - tx, connectorMousePos.y - (ty + th / 2)),
              };

              const sorted = Object.entries(distances).sort((a, b) => a[1] - b[1]);
              const previewEdge = sorted[0][0] as 'top' | 'right' | 'bottom' | 'left';
              const endPt = getNodeEdgeCoords(targetNode.id, previewEdge);

              const dx = Math.abs(endPt.x - startPt.x) * 0.5;
              const dy = Math.abs(endPt.y - startPt.y) * 0.5;

              let cx1 = startPt.x;
              let cy1 = startPt.y;
              let cx2 = endPt.x;
              let cy2 = endPt.y;

              if (activeConnectorSource.edge === 'left') cx1 -= dx;
              else if (activeConnectorSource.edge === 'right') cx1 += dx;
              else if (activeConnectorSource.edge === 'top') cy1 -= dy;
              else if (activeConnectorSource.edge === 'bottom') cy1 += dy;

              if (previewEdge === 'left') cx2 -= dx;
              else if (previewEdge === 'right') cx2 += dx;
              else if (previewEdge === 'top') cy2 -= dy;
              else if (previewEdge === 'bottom') cy2 += dy;

              pathData = `M ${startPt.x} ${startPt.y} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${endPt.x} ${endPt.y}`;
            }

            return (
              <path
                d={pathData}
                fill="none"
                stroke={activeColor}
                strokeWidth={2}
                strokeDasharray="5, 5"
                markerEnd="url(#whiteboard-arrowhead-preview)"
              />
            );
          })()}

          {/* Render Freehand Drawings */}
          {decorations.filter(d => d.type === 'path').map(d => {
            if (!d.points || d.points.length === 0) return null;
            const pathData = d.points.reduce((acc, pt, idx) => {
              if (idx === 0) return `M ${pt.x} ${pt.y}`;
              return `${acc} L ${pt.x} ${pt.y}`;
            }, '');

            return (
              <path
                key={d.id}
                d={pathData}
                fill="none"
                stroke={d.color || '#000'}
                strokeWidth={d.lineWidth || 4}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={d.opacity || 1}
                className="pointer-events-auto cursor-pointer"
                data-element-id={d.id}
                onPointerDown={(e) => {
                  if (activeTool === 'eraser') {
                    // Let it bubble up to handlePointerDown on viewport for mathematical point erasing
                    return;
                  }
                  e.stopPropagation();
                  setSelectedIds([d.id]);
                }}
              />
            );
          })}

          {/* Active drawing path preview */}
          {isDrawing && (activeTool === 'pen' || activeTool === 'highlighter') && currentPathPoints.length > 0 && (
            <path
              d={currentPathPoints.reduce((acc, pt, idx) => {
                if (idx === 0) return `M ${pt.x} ${pt.y}`;
                return `${acc} L ${pt.x} ${pt.y}`;
              }, '')}
              fill="none"
              stroke={activeColor}
              strokeWidth={brushWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={activeTool === 'highlighter' ? 0.45 : 1}
            />
          )}
        </svg>

        {/* Translucent Marquee Selection box */}
        {marqueeStart && marqueeEnd && (
          <div
            className="absolute border border-dashed border-blue-500 bg-blue-500/10 pointer-events-none rounded-sm z-30"
            style={{
              left: Math.min(marqueeStart.x, marqueeEnd.x),
              top: Math.min(marqueeStart.y, marqueeEnd.y),
              width: Math.abs(marqueeStart.x - marqueeEnd.x),
              height: Math.abs(marqueeStart.y - marqueeEnd.y),
            }}
          />
        )}
      </div>

      {/* ── CANVAS VIEWPORT CONTROLS FLOATING PANEL ── */}
      <div className="absolute bottom-4 left-16 flex items-center gap-2 z-10 select-none">
        {/* Zoom Controls HUD */}
        <div className="flex items-center gap-1.5 bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 rounded-full px-3 py-1 shadow-md">
          <button 
            onClick={() => setZoom(prev => Math.max(0.25, prev - 0.1))}
            className="w-6 h-6 hover:bg-slate-100 dark:hover:bg-slate-850 rounded-full flex items-center justify-center font-bold text-slate-600 dark:text-zinc-350 cursor-pointer"
          >
            -
          </button>
          <span className="text-[11px] font-bold font-mono text-slate-700 dark:text-zinc-300 w-11 text-center select-none">
            {Math.round(zoom * 100)}%
          </span>
          <button 
            onClick={() => setZoom(prev => Math.min(4.0, prev + 0.1))}
            className="w-6 h-6 hover:bg-slate-100 dark:hover:bg-slate-850 rounded-full flex items-center justify-center font-bold text-slate-600 dark:text-zinc-350 cursor-pointer"
          >
            +
          </button>
        </div>

        {/* Background style indicator selector */}
        <div className="flex items-center gap-1 bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 rounded-full p-0.5 shadow-md">
          {(['dots', 'grid'] as const).map(style => (
            <button
              key={style}
              onClick={() => {
                setBackgroundStyle(style);
                setGridVisible(true);
              }}
              className={`text-[9.5px] font-bold px-2.5 py-1.5 rounded-full capitalize cursor-pointer transition-colors ${
                backgroundStyle === style 
                  ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-950 font-black' 
                  : 'text-slate-450 hover:text-slate-700 dark:hover:text-white'
              }`}
            >
              {style === 'dots' ? 'Default Dots' : 'Default Grid'}
            </button>
          ))}
        </div>
      </div>

      {/* ── MINIMAP PANEL BOTTOM RIGHT ── */}
      <div className="absolute bottom-4 right-4 z-10">
        {workspaceRef.current && (
          <Minimap
            elements={getFilteredNodes()}
            zoom={zoom}
            pan={pan}
            workspaceWidth={workspaceRef.current.clientWidth || 800}
            workspaceHeight={workspaceRef.current.clientHeight || 600}
            onPanChange={setPan}
          />
        )}
      </div>

      {/* ── EMPTY STATE ANIMS ── */}
      {filteredCards.length === 0 && decorations.length === 0 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none select-none p-4 max-w-sm mx-auto z-0 animate-in fade-in zoom-in-95 duration-400">
          <div className="w-20 h-20 bg-indigo-50/70 dark:bg-indigo-950/20 border border-indigo-200/50 rounded-2xl flex items-center justify-center mb-6 shadow-sm animate-pulse">
            <LayoutGrid className="w-10 h-10 text-indigo-500" />
          </div>
          <h3 className="text-sm font-extrabold text-slate-800 dark:text-white font-sans mb-1 uppercase tracking-wider">
            Distraction-Free Brainstorming Board
          </h3>
          <p className="text-[11px] text-slate-400 leading-relaxed font-sans">
            Double-click anywhere on the whiteboard dot grid to post sticky thoughts or insert shape links using the left vertical floating toolbar.
          </p>
        </div>
      )}

      {/* FLOATING COLLABORATION COMMENTS LOG DIALOG CARD */}
      {activeCommentId && (
        <div className="absolute top-16 right-4 w-[280px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl shadow-2xl z-40 animate-in slide-in-from-right-3 duration-150 text-slate-800 dark:text-zinc-150">
          <div className="flex justify-between items-center pb-2 border-b border-slate-100 dark:border-slate-805 mb-3 select-none">
            <h4 className="font-bold text-xs flex items-center gap-1.5 uppercase tracking-wide text-blue-500">
              <MessageSquare className="w-3.5 h-3.5 fill-current" />
              <span>Annotation Thread</span>
            </h4>
            <button
              onClick={() => {
                setActiveCommentId(null);
                setNewCommentText('');
              }}
              className="text-slate-400 hover:text-slate-605 font-bold text-xs p-0.5 rounded-md"
            >
              ×
            </button>
          </div>

          {/* Scrolling existing replies */}
          <div className="space-y-3 max-h-48 overflow-y-auto pr-1 auto-scrollbar mb-3">
            {(decorations.find((el) => el.id === activeCommentId)?.comments || []).map((repl) => (
              <div key={repl.id} className="text-xs bg-slate-50 dark:bg-slate-950 p-2.5 rounded-xl border border-slate-100 dark:border-slate-850">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-slate-900 dark:text-white flex items-center gap-1">
                    <User className="w-3 h-3 text-slate-400" />
                    {repl.author}
                  </span>
                  <span className="text-[9px] font-mono text-slate-400">
                    {new Date(repl.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <p className="text-slate-655 dark:text-slate-300 break-words font-medium leading-relaxed">{repl.text}</p>
              </div>
            ))}
            {(decorations.find((el) => el.id === activeCommentId)?.comments || []).length === 0 && (
              <span className="text-[10px] text-slate-400 block text-center py-2">No annotation replies yet.</span>
            )}
          </div>

          {/* Dialog entry form */}
          <form onSubmit={handleAddCommentToThread} className="flex flex-col gap-2">
            <input
              type="text"
              placeholder="Your Name (e.g. Mithun)"
              value={newCommentAuthor}
              onChange={(e) => setNewCommentAuthor(e.target.value)}
              className="px-2.5 py-1.5 border border-slate-250 dark:border-slate-750 bg-slate-50 dark:bg-slate-950 text-xs rounded-lg dark:text-white font-medium select-text"
              style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
            />
            <textarea
              placeholder="Write feedback remarks..."
              value={newCommentText}
              onChange={(e) => setNewCommentText(e.target.value)}
              className="px-2.5 py-1.5 border border-slate-250 dark:border-slate-750 bg-slate-50 dark:bg-slate-950 text-xs rounded-lg dark:text-white h-14 resize-none focus:outline-none select-text"
              style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
              required
            />
            <div className="flex gap-1.5 justify-end mt-1">
              <button
                type="button"
                onClick={() => {
                  pushToHistory();
                  setDecorations(decorations.filter((item) => item.id !== activeCommentId));
                  setActiveCommentId(null);
                  setSelectedIds([]);
                }}
                className="px-2 py-1.5 text-[10px] font-bold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-955/20 rounded-md shrink-0 flex items-center gap-1 border border-transparent"
                title="Wipe comment balloon"
              >
                <Trash2 className="w-3 h-3" />
                <span>Delete Thread</span>
              </button>

              <button
                type="submit"
                className="px-3 py-1.5 text-[10px] font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center gap-1 text-center"
              >
                <Check className="w-3 h-3" />
                <span>Post note</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── CONTEXT MENU PROPERTIES ADJUSTER ── */}
      {contextMenuPos && (
        <div
          id="whiteboard-context-menu-wrapper"
          className="absolute z-50 shadow-2xl transition-all animate-in fade-in zoom-in-95 duration-100"
          style={getContextMenuStyles()}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onPointerEnter={(e) => { e.stopPropagation(); setMouseScreenPos(null); }}
        >
          {/* 1. Brush selector for drawing tools */}
          {(activeTool === 'pen' || activeTool === 'highlighter') && (
            <BrushSelector
              color={activeColor}
              setColor={setActiveColor}
              lineWidth={brushWidth}
              setLineWidth={setBrushWidth}
              brushColors={BRUSH_COLORS}
              isActive={true}
            />
          )}

          {/* 2. Eraser control */}
          {activeTool === 'eraser' && (
            <EraserControl
              lineWidth={eraserWidth}
              setLineWidth={setEraserWidth}
              isActive={true}
            />
          )}

          {/* 3. Sticky Note properties context menu */}
          {contextMenuTarget?.type === 'card' && (() => {
            const cardId = contextMenuTarget.id;
            const card = cards.find(c => c.id === cardId);
            if (!card) return null;
            return (
              <div className="flex flex-col gap-3 p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 w-[220px]">
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-1.5 mb-1">
                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Note Properties</span>
                  <button
                    onClick={() => {
                      pushToHistory();
                      setCards(cards.map(c => c.id === cardId ? { ...c, isDeleted: true, deletedAt: Date.now() } : c));
                      saveCanvasState(decorations, connectors, groups, cards.map(c => c.id === cardId ? { ...c, isDeleted: true, deletedAt: Date.now() } : c));
                      setContextMenuPos(null);
                    }}
                    className="text-[10px] text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-955/20 px-1.5 py-0.5 rounded border border-transparent font-bold flex items-center gap-1 cursor-pointer"
                    title="Delete Note"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>

                {/* Color presets */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-[10px] text-slate-400 font-bold uppercase">Background Color</span>
                  <div className="grid grid-cols-5 gap-1">
                    {PASTEL_COLORS.map((col) => (
                      <button
                        key={col.name}
                        onClick={() => {
                          pushToHistory();
                          setCards(cards.map(c => c.id === cardId ? { ...c, boardColor: col.fill } : c));
                          saveCanvasState(decorations, connectors, groups, cards.map(c => c.id === cardId ? { ...c, boardColor: col.fill } : c));
                        }}
                        className={`w-6 h-6 rounded-md border transition-transform ${
                          card.boardColor === col.fill ? 'ring-2 ring-blue-500 border-white' : 'border-slate-200/50 dark:border-slate-800'
                        }`}
                        style={{ backgroundColor: col.fill }}
                        title={col.name}
                      />
                    ))}
                  </div>
                </div>

                {/* Lock Toggle */}
                <button
                  onClick={() => {
                    pushToHistory();
                    setCards(cards.map(c => c.id === cardId ? { ...c, isLocked: !c.isLocked } : c));
                    saveCanvasState(decorations, connectors, groups, cards.map(c => c.id === cardId ? { ...c, isLocked: !c.isLocked } : c));
                  }}
                  className="w-full flex items-center justify-between text-xs font-semibold py-1.5 px-2.5 rounded-lg bg-slate-50 dark:bg-slate-850 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-zinc-300 border border-slate-150 dark:border-slate-800 cursor-pointer transition-colors"
                >
                  <span className="flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5" /> Lock Position
                  </span>
                  <span className="text-[10px] uppercase font-mono text-slate-400">{card.isLocked ? 'ON' : 'OFF'}</span>
                </button>
              </div>
            );
          })()}

          {/* 4. Decoration (shape/text) properties menu */}
          {contextMenuTarget?.type === 'decoration' && (() => {
            const decId = contextMenuTarget.id;
            const dec = decorations.find(d => d.id === decId);
            if (!dec) return null;
            return (
              <div className="flex flex-col gap-3 p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 w-[220px]">
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-1.5 mb-1">
                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Item Properties</span>
                  <button
                    onClick={() => {
                      pushToHistory();
                      setDecorations(decorations.filter(d => d.id !== decId));
                      saveCanvasState(decorations.filter(d => d.id !== decId), connectors, groups);
                      setContextMenuPos(null);
                    }}
                    className="text-[10px] text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-955/20 px-1.5 py-0.5 rounded border border-transparent font-bold flex items-center gap-1 cursor-pointer"
                    title="Delete Item"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>

                {/* Color presets */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-[10px] text-slate-400 font-bold uppercase">Color Theme</span>
                  <div className="grid grid-cols-4 gap-1.5">
                    {BRUSH_COLORS.map((col) => (
                      <button
                        key={col}
                        onClick={() => {
                          pushToHistory();
                          setDecorations(decorations.map(d => d.id === decId ? { 
                            ...d, 
                            color: col,
                            fillColor: d.type === 'shape' ? col + '15' : d.fillColor 
                          } : d));
                          saveCanvasState(decorations.map(d => d.id === decId ? { 
                            ...d, 
                            color: col,
                            fillColor: d.type === 'shape' ? col + '15' : d.fillColor 
                          } : d), connectors, groups);
                        }}
                        className={`w-6.5 h-6.5 rounded-full border transition-transform ${
                          dec.color === col ? 'ring-2 ring-blue-500 border-white' : 'border-slate-200/50 dark:border-slate-800'
                        }`}
                        style={{ backgroundColor: col }}
                        title={col}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* 5. General Canvas properties context menu (when activeTool is select) */}
          {contextMenuTarget?.type === 'canvas' && !['pen', 'highlighter', 'eraser'].includes(activeTool) && (
            <div className="flex flex-col gap-3 p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 w-[200px]">
              <div className="border-b border-slate-100 dark:border-slate-800 pb-1.5 mb-1">
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Canvas Settings</span>
              </div>

              {/* Grid Toggle */}
              <button
                onClick={() => setGridVisible(!gridVisible)}
                className="w-full flex items-center justify-between text-xs font-semibold py-1.5 px-2.5 rounded-lg bg-slate-50 dark:bg-slate-850 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-zinc-300 border border-slate-150 dark:border-slate-800 cursor-pointer transition-colors"
              >
                <span className="flex items-center gap-1.5">
                  <Grid className="w-3.5 h-3.5" /> Grid Visible
                </span>
                <span className="text-[10px] uppercase font-mono text-slate-400">{gridVisible ? 'ON' : 'OFF'}</span>
              </button>

              {/* Background selector */}
              <div className="flex flex-col gap-1.5 mt-1">
                <span className="text-[10px] text-slate-400 font-bold uppercase">Background Style</span>
                <div className="flex flex-col gap-1">
                  {(['dots', 'grid'] as const).map(style => (
                    <button
                      key={style}
                      onClick={() => {
                        setBackgroundStyle(style);
                        setGridVisible(true);
                      }}
                      className={`text-xs text-left font-semibold py-1 px-2.5 rounded-md capitalize cursor-pointer transition-colors ${
                        backgroundStyle === style 
                          ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-950 font-black' 
                          : 'text-slate-600 hover:bg-slate-55 dark:text-slate-300 dark:hover:bg-slate-800'
                      }`}
                    >
                      {style === 'dots' ? 'Default Dots' : 'Default Grid'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Custom drawing/eraser cursors */}
      {activeTool === 'eraser' && mouseScreenPos && workspaceRef.current && (() => {
        const rect = workspaceRef.current.getBoundingClientRect();
        const left = mouseScreenPos.x - rect.left;
        const top = mouseScreenPos.y - rect.top;
        return (
          <div
            className="absolute border border-indigo-500 bg-indigo-50/15 rounded-full pointer-events-none z-50 transition-all duration-75 ease-out"
            style={{
              left: left - eraserWidth / 2,
              top: top - eraserWidth / 2,
              width: eraserWidth,
              height: eraserWidth,
            }}
          />
        );
      })()}

      {(activeTool === 'pen' || activeTool === 'highlighter') && mouseScreenPos && workspaceRef.current && (() => {
        const rect = workspaceRef.current.getBoundingClientRect();
        const left = mouseScreenPos.x - rect.left;
        const top = mouseScreenPos.y - rect.top;
        const size = brushWidth * zoom;
        return (
          <div
            className="absolute border border-slate-450 bg-slate-200/25 rounded-full pointer-events-none z-50 transition-all duration-75 ease-out"
            style={{
              left: left - size / 2,
              top: top - size / 2,
              width: size,
              height: size,
            }}
          />
        );
      })()}

      {/* ── CUSTOM WEBPAGE ALERT/CONFIRM POPUP MODAL ── */}
      {confirmDialog && confirmDialog.isOpen && (
        <div 
          className="absolute inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center z-50 animate-in fade-in duration-150"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl max-w-sm w-full mx-4 flex flex-col gap-4 animate-in zoom-in-95 duration-200">
            <h3 className="text-sm font-extrabold text-slate-800 dark:text-white uppercase tracking-wider">{confirmDialog.title}</h3>
            <p className="text-xs font-semibold text-slate-500 dark:text-zinc-400 leading-relaxed">{confirmDialog.message}</p>
            <div className="flex gap-2 justify-end mt-2">
              <button
                onClick={() => setConfirmDialog(null)}
                className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-850 rounded-xl transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  confirmDialog.onConfirm();
                  setConfirmDialog(null);
                }}
                className="px-4 py-2 text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white rounded-xl transition-colors shadow-md active:scale-95 cursor-pointer"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connection Animation keyframes stylesheet */}
      <style>{`
        .whiteboard-connector-dash-line {
          animation: whiteboardConnectorDashAnim 15s linear infinite;
        }
        @keyframes whiteboardConnectorDashAnim {
          from {
            stroke-dashoffset: 200;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </div>
  );
};
